import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import L from "leaflet";
import {
  Circle,
  GeoJSON,
  MapContainer,
  Marker,
  Popup,
  Polyline,
  TileLayer,
  useMap,
  useMapEvents
} from "react-leaflet";
import type { LatLng, LatLngExpression, LeafletMouseEvent } from "leaflet";
import type { Feature, MultiPolygon, Polygon } from "geojson";
import type {
  CursorPresence,
  DistanceUnit,
  EditingPresence,
  MapViewPresence,
  MapViewState,
  Measurement,
  MeasurementType,
  NorthReference,
  PresenceUser,
  SavedTarget,
  SessionData,
  SessionMeta
} from "./types";
import {
  computeConfidenceRegions,
  estimateCenter,
  estimateCenters,
  intersectAll,
  circleIntersections,
  measurementToGeometry,
  bearingToTrue
} from "./utils/geometry";
import {
  clearSession,
  loadCallsign,
  loadMapView,
  loadLegacyTargets,
  loadLegacySession,
  loadOrCreateUserId,
  loadSession,
  saveCallsign,
  saveMapView,
  saveSession
} from "./utils/storage";
import {
  formatBearing,
  formatCoordinate,
  formatDateTime,
  formatDistance
} from "./utils/format";
import { reverseGeocode } from "./utils/geocode";
import { toPlusCode } from "./utils/plusCode";
import {
  createSession,
  getCollabWsUrl,
  isValidSessionId,
  normalizeSessionId
} from "./utils/collab";
import {
  canonicalizeCallsign,
  isCallsignTaken,
  normalizeCallsign,
  resolveInitialCallsignDraft
} from "./utils/callsign";
import {
  applyCursorPresenceUpdate,
  buildCursorPresenceMap,
  filterCursorPresenceByRoster,
  projectCursorsToViewport
} from "./utils/cursorPresence";
import {
  applyMapViewPresenceUpdate,
  buildMapViewPresenceMap,
  filterMapViewPresenceByRoster
} from "./utils/mapViewPresence";
import {
  collectPointsForFit,
  getPointFitAction,
  getUserFitAction
} from "./utils/mapViewport";
import {
  geometryKeySeedFromMeasurements,
  hasSingleDistanceUncertainty,
  measurementGeometryKey
} from "./utils/measurementPresentation";
import {
  buildTelemetryContext,
  setTelemetryUserIdentity,
  TELEMETRY_EVENTS,
  trackTelemetry
} from "./utils/telemetry";
import type { TelemetryEventName, TelemetryParams } from "./utils/telemetry";
import { CallsignDialog } from "./components/CallsignDialog";
import { ViewSettingsMenu } from "./components/ViewSettingsMenu";

const DEFAULT_CENTER: MapViewState = {
  lat: 39.8283,
  lon: -98.5795,
  zoom: 4
};

const DEFAULT_DECLINATION = 0;
const CURSOR_THROTTLE_MS = 75;
const FIT_PADDING_RATIO = 0.1;
const SIMULATED_CURSOR_COUNT = 5;
const SIMULATED_CURSOR_INTERVAL_MS = 1500;

const markerColors = {
  pending: "#e0c55a",
  added: "#3b9b7d",
  estimate: "#2d6cdf",
  outlier: "#d94b4b",
  saved: "#c5533f"
} as const;

const simulatedCursorProfiles = [
  { userId: "sim-user-1", callsign: "Sim Alpha", color: "#2d6cdf" },
  { userId: "sim-user-2", callsign: "Sim Bravo", color: "#3b9b7d" },
  { userId: "sim-user-3", callsign: "Sim Charlie", color: "#c36f43" },
  { userId: "sim-user-4", callsign: "Sim Delta", color: "#8a5cf6" },
  { userId: "sim-user-5", callsign: "Sim Echo", color: "#d94b4b" }
];

const randomBetween = (min: number, max: number) =>
  min + Math.random() * (max - min);

const randomPointInMapBounds = (map: L.Map): { lat: number; lon: number } => {
  const bounds = map.getBounds();
  const southWest = bounds.getSouthWest();
  const northEast = bounds.getNorthEast();
  const latMin = Math.min(southWest.lat, northEast.lat);
  const latMax = Math.max(southWest.lat, northEast.lat);
  const lonMin = Math.min(southWest.lng, northEast.lng);
  const lonMax = Math.max(southWest.lng, northEast.lng);
  if (
    !Number.isFinite(latMin) ||
    !Number.isFinite(latMax) ||
    !Number.isFinite(lonMin) ||
    !Number.isFinite(lonMax) ||
    latMin === latMax ||
    lonMin === lonMax
  ) {
    const center = map.getCenter();
    return {
      lat: center.lat,
      lon: center.lng
    };
  }
  return {
    lat: randomBetween(latMin, latMax),
    lon: randomBetween(lonMin, lonMax)
  };
};

const createPinSvg = (color: string) => `<?xml version="1.0" encoding="UTF-8"?>
<svg width="28" height="40" viewBox="0 0 28 40" xmlns="http://www.w3.org/2000/svg">
  <path d="M14 1.5C7.37 1.5 2 6.87 2 13.5c0 9.02 12 25 12 25s12-15.98 12-25C26 6.87 20.63 1.5 14 1.5z" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
  <circle cx="14" cy="13.5" r="4.4" fill="#ffffff" fill-opacity="0.92"/>
</svg>`;

const buildPinIcon = (color: string) =>
  L.icon({
    iconUrl: `data:image/svg+xml,${encodeURIComponent(createPinSvg(color))}`,
    iconSize: [28, 40],
    iconAnchor: [14, 38],
    popupAnchor: [0, -34],
    className: "pin-icon"
  });

const buildExclusionSets = (total: number, count: number) => {
  const results: number[][] = [];
  const walk = (start: number, acc: number[]) => {
    if (acc.length === count) {
      results.push([...acc]);
      return;
    }
    for (let i = start; i <= total - (count - acc.length); i += 1) {
      acc.push(i);
      walk(i + 1, acc);
      acc.pop();
    }
  };
  walk(0, []);
  return results;
};

const haversineMeters = (a: { lat: number; lon: number }, b: { lat: number; lon: number }) => {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const h =
    sinLat * sinLat +
    Math.cos(lat1) * Math.cos(lat2) * sinLon * sinLon;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
};

const upsertById = <T extends { id: string }>(items: T[], next: T) => {
  const index = items.findIndex((item) => item.id === next.id);
  if (index === -1) return [...items, next];
  const copy = [...items];
  copy[index] = next;
  return copy;
};

const abbreviateCallsign = (callsign: string) =>
  callsign
    .trim()
    .split(/\s+/)
    .map((word) => word.slice(0, 1))
    .join("")
    .slice(0, 2)
    .toUpperCase() || "??";

type ConsensusEstimate = {
  id: string;
  lat: number;
  lon: number;
  excludedCount: number;
  excludedIds: string[];
};

const computeConsensusEstimates = (
  items: { measurement: Measurement; polygon: Feature<Polygon | MultiPolygon> }[],
  maxExclusions = 3
) => {
  const total = items.length;
  const maxDrop = Math.min(maxExclusions, total - 2);
  for (let drop = 1; drop <= maxDrop; drop += 1) {
    const excludeSets = buildExclusionSets(total, drop);
    const candidates: ConsensusEstimate[] = [];
    excludeSets.forEach((exclude) => {
      const excludeSet = new Set(exclude);
      const kept = items.filter((_, index) => !excludeSet.has(index));
      if (kept.length < 2) return;
      const intersection = intersectAll(kept.map((item) => item.polygon));
      if (!intersection) return;
      const center = estimateCenter(intersection);
      candidates.push({
        id: `${drop}-${exclude.join("-")}`,
        lat: center.lat,
        lon: center.lon,
        excludedCount: drop,
        excludedIds: exclude.map((index) => items[index].measurement.id)
      });
    });
    if (candidates.length > 0) {
      const deduped: ConsensusEstimate[] = [];
      candidates.forEach((candidate) => {
        const near = deduped.find(
          (existing) =>
            haversineMeters(existing, candidate) < 350
        );
        if (!near) {
          deduped.push(candidate);
        }
      });
      return deduped;
    }
  }
  return [];
};

type MeasurementDraft = {
  lat: number;
  lon: number;
  type: MeasurementType;
  value: number;
  units: DistanceUnit;
  northReference: NorthReference;
  declination: number;
  distanceErrorPct?: number;
  bearingErrorDeg?: number;
};

type SavedTargetDraft = {
  id: string;
  version: number;
  name: string;
  lat: number;
  lon: number;
  visible: boolean;
};

type MapPopout =
  | { kind: "draft"; lat: number; lon: number; mode: "menu" | "add" }
  | { kind: "measurement"; id: string; mode: "view" | "edit" }
  | null;

const isEventTargetInsideLeafletPopup = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest(".leaflet-popup"));

const isTextSelectableInputTarget = (target: EventTarget | null) => {
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) {
    if (target.disabled || target.readOnly) return false;
    return !["button", "checkbox", "color", "file", "hidden", "image", "radio", "range", "reset", "submit"].includes(target.type);
  }
  return target instanceof HTMLElement && target.isContentEditable;
};

const isDecimalInputToken = (raw: string, allowNegative = false) =>
  allowNegative
    ? /^-?\d*(?:\.\d*)?$/.test(raw)
    : /^\d*(?:\.\d*)?$/.test(raw);

const isIncompleteDecimalToken = (raw: string) =>
  raw === "" || raw === "." || raw === "-" || raw === "-.";

const displayNumericFieldValue = (value: number | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? value : "";

const emptyDraftFromPoint = (lat: number, lon: number): MeasurementDraft => ({
  lat,
  lon,
  type: "bearing",
  value: 0,
  units: "km",
  northReference: "true",
  declination: DEFAULT_DECLINATION,
  distanceErrorPct: undefined,
  bearingErrorDeg: undefined
});

const MapEvents = ({
  onClick,
  onMove,
  onCursorMove,
  onCursorLeave
}: {
  onClick: (latlng: LatLng) => void;
  onMove: (view: MapViewState) => void;
  onCursorMove: (latlng: LatLng) => void;
  onCursorLeave: () => void;
}) => {
  const map = useMapEvents({
    click: (event: LeafletMouseEvent) => {
      const target = event.originalEvent?.target;
      if (
        isEventTargetInsideLeafletPopup(target) ||
        (target instanceof Element && target.closest(".map-zoom-controls"))
      ) {
        return;
      }
      onClick(event.latlng);
    },
    moveend: () => {
      const center = map.getCenter();
      onMove({ lat: center.lat, lon: center.lng, zoom: map.getZoom() });
    },
    mousemove: (event: LeafletMouseEvent) => {
      onCursorMove(event.latlng);
    },
    mouseout: () => {
      onCursorLeave();
    }
  });

  return null;
};

const MapInstanceBridge = ({
  onMapReady
}: {
  onMapReady: (map: L.Map | null) => void;
}) => {
  const map = useMap();

  useEffect(() => {
    onMapReady(map);
    return () => {
      onMapReady(null);
    };
  }, [map, onMapReady]);

  return null;
};

const MapZoomControls = ({
  onZoomToFitPoints,
  onZoomToFitUsers
}: {
  onZoomToFitPoints: (map: L.Map) => void;
  onZoomToFitUsers: (map: L.Map) => void;
}) => {
  const map = useMap();

  return createPortal(
    <div className="map-zoom-controls" role="group" aria-label="Map zoom controls">
      <button
        className="map-zoom-button"
        type="button"
        aria-label="Zoom in"
        onClick={() => map.zoomIn()}
      >
        +
      </button>
      <button
        className="map-zoom-button"
        type="button"
        aria-label="Zoom out"
        onClick={() => map.zoomOut()}
      >
        -
      </button>
      <button
        className="map-zoom-button map-zoom-action"
        type="button"
        aria-label="Zoom to fit all points"
        onClick={() => onZoomToFitPoints(map)}
      >
        Fit points
      </button>
      <button
        className="map-zoom-button map-zoom-action"
        type="button"
        aria-label="Zoom to fit all users"
        onClick={() => onZoomToFitUsers(map)}
      >
        Fit users
      </button>
    </div>,
    map.getContainer()
  );
};

const MapFocus = ({
  focus
}: {
  focus: { lat: number; lon: number } | null;
}) => {
  const map = useMap();

  useEffect(() => {
    if (!focus) return;
    map.flyTo([focus.lat, focus.lon], Math.max(map.getZoom(), 10), {
      duration: 0.6
    });
  }, [focus, map]);

  return null;
};

const MapSizeSync = () => {
  const map = useMap();

  useEffect(() => {
    let frameA = 0;
    let frameB = 0;
    const container = map.getContainer();

    const invalidate = () => {
      map.invalidateSize({
        pan: false,
        debounceMoveend: true
      });
    };

    const scheduleInvalidate = () => {
      cancelAnimationFrame(frameA);
      cancelAnimationFrame(frameB);
      frameA = requestAnimationFrame(() => {
        invalidate();
        frameB = requestAnimationFrame(invalidate);
      });
    };

    scheduleInvalidate();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => {
        scheduleInvalidate();
      });
      observer.observe(container);
      return () => {
        cancelAnimationFrame(frameA);
        cancelAnimationFrame(frameB);
        observer.disconnect();
      };
    }

    window.addEventListener("resize", scheduleInvalidate);
    return () => {
      cancelAnimationFrame(frameA);
      cancelAnimationFrame(frameB);
      window.removeEventListener("resize", scheduleInvalidate);
    };
  }, [map]);

  return null;
};

const RemoteCursorOverlay = ({
  cursors
}: {
  cursors: CursorPresence[];
}) => {
  const map = useMap();
  const [, setMapRevision] = useState(0);

  useEffect(() => {
    const refresh = () => {
      setMapRevision((value) => value + 1);
    };
    map.on("move", refresh);
    map.on("zoom", refresh);
    map.on("resize", refresh);
    return () => {
      map.off("move", refresh);
      map.off("zoom", refresh);
      map.off("resize", refresh);
    };
  }, [map]);

  const size = map.getSize();
  const projected = projectCursorsToViewport({
    cursors,
    viewportWidth: size.x,
    viewportHeight: size.y,
    project: (lat, lon) => {
      const point = map.latLngToContainerPoint([lat, lon]);
      return { x: point.x, y: point.y };
    }
  });

  return createPortal(
    <div className="remote-cursor-overlay" data-testid="remote-cursor-overlay">
      {projected.map((cursor) => (
        <div
          key={cursor.userId}
          className="remote-cursor"
          style={{ transform: `translate(${cursor.x}px, ${cursor.y}px)` }}
        >
          <svg
            className="remote-cursor-pointer"
            viewBox="0 0 31 32"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12.5448 26L6.72762 5.59339L26 14.8132L17.1816 17.9377L12.5448 26Z"
              fill={cursor.color}
            />
            <path
              d="M6.9457 5.14539L26.2178 14.3651L27.2985 14.8826L26.1697 15.2813L17.5259 18.3444L12.9812 26.2464L12.3881 27.2792L12.0614 26.1349L6.24434 5.72851L5.94119 4.66489L6.9457 5.14539Z"
              stroke="#F5FAFF"
              strokeWidth="1.1"
              strokeLinejoin="round"
            />
          </svg>
          <span
            className="remote-cursor-label"
            style={{ backgroundColor: cursor.color }}
          >
            {cursor.callsign}
          </span>
        </div>
      ))}
    </div>,
    map.getContainer()
  );
};

const buildSession = (
  measurements: Measurement[],
  sessionMeta: SessionMeta | null,
  savedTargets: SavedTarget[]
): SessionData => ({
  version: "1",
  createdAt: sessionMeta?.createdAt ?? new Date().toISOString(),
  lastEditedAt: sessionMeta?.lastEditedAt,
  measurements,
  savedTargets
});

const distanceUnits: DistanceUnit[] = ["km", "mi", "m"];

const formatDistanceWithError = (measurement: Measurement) => {
  const base = formatDistance(measurement.value, measurement.units);
  const errorPct = measurement.distanceErrorPct ?? 0;
  if (errorPct > 0) {
    return `${base} ±${errorPct.toFixed(1)}%`;
  }
  return base;
};

const formatDistanceRange = (measurement: Measurement) => {
  const errorPct = measurement.distanceErrorPct ?? 0;
  if (errorPct <= 0) return null;
  const delta = measurement.value * (errorPct / 100);
  const min = measurement.value - delta;
  const max = measurement.value + delta;
  return `${min.toFixed(2)}-${max.toFixed(2)} ${measurement.units}`;
};

const formatBearingWithError = (measurement: Measurement) => {
  const errorDeg = measurement.bearingErrorDeg ?? 0;
  const errorSuffix = errorDeg > 0 ? ` ±${formatBearing(errorDeg)}` : "";
  return `${formatBearing(measurement.value)} (${measurement.northReference})${errorSuffix}`;
};

const formatTrueBearingWithError = (measurement: Measurement) => {
  const errorDeg = measurement.bearingErrorDeg ?? 0;
  const errorSuffix = errorDeg > 0 ? ` ±${formatBearing(errorDeg)}` : "";
  return `${formatBearing(bearingToTrue(measurement))}${errorSuffix}`;
};

const classifyLookupError = (error: unknown) => {
  if (error instanceof DOMException && error.name === "AbortError") {
    return "timeout";
  }
  return "lookup_failed";
};

const classifyServerMessage = (message: string) => {
  if (/invalid/i.test(message)) return "invalid_payload";
  if (/conflict/i.test(message)) return "conflict";
  if (/missing/i.test(message)) return "missing_payload";
  return "server_error";
};

function App() {
  const markerIcons = useMemo(
    () => ({
      pending: buildPinIcon(markerColors.pending),
      added: buildPinIcon(markerColors.added),
      estimate: buildPinIcon(markerColors.estimate),
      outlier: buildPinIcon(markerColors.outlier),
      saved: buildPinIcon(markerColors.saved)
    }),
    []
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionMeta, setSessionMeta] = useState<SessionMeta | null>(null);
  const [collabStatus, setCollabStatus] = useState<
    "idle" | "connecting" | "connected" | "disconnected"
  >("idle");
  const [presence, setPresence] = useState<PresenceUser[]>([]);
  const [remoteCursors, setRemoteCursors] = useState<
    Record<string, CursorPresence>
  >({});
  const [remoteMapViews, setRemoteMapViews] = useState<
    Record<string, MapViewPresence>
  >({});
  const [editingPresence, setEditingPresence] = useState<EditingPresence[]>([]);
  const [callsign, setCallsign] = useState(() =>
    normalizeCallsign(loadCallsign() ?? "")
  );
  const [callsignDraft, setCallsignDraft] = useState("");
  const [callsignDialogMode, setCallsignDialogMode] = useState<
    "join" | "edit" | null
  >(null);
  const [callsignError, setCallsignError] = useState<string | null>(null);
  const [callsignSubmitting, setCallsignSubmitting] = useState(false);
  const [pendingCallsignSubmission, setPendingCallsignSubmission] = useState<
    string | null
  >(null);
  const [joinedSession, setJoinedSession] = useState(false);
  const [userId] = useState(() => loadOrCreateUserId());
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [savedTargets, setSavedTargets] = useState<SavedTarget[]>([]);
  const [selectedMeasurementId, setSelectedMeasurementId] = useState<
    string | null
  >(null);
  const [draft, setDraft] = useState<MeasurementDraft | null>(null);
  const [formMode, setFormMode] = useState<"add" | "edit" | null>(null);
  const [mapPopout, setMapPopout] = useState<MapPopout>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formNotice, setFormNotice] = useState<string | null>(null);
  const [editingVersion, setEditingVersion] = useState<number | null>(null);
  const [targetDraft, setTargetDraft] = useState<SavedTargetDraft | null>(null);
  const [targetEditNotice, setTargetEditNotice] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [geocodeEnabled, setGeocodeEnabled] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [showRemoteCursors, setShowRemoteCursors] = useState(true);
  const [simulateRandomCursors, setSimulateRandomCursors] = useState(false);
  const [simulatedCursors, setSimulatedCursors] = useState<CursorPresence[]>([]);
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  const [mapView, setMapView] = useState<MapViewState>(
    loadMapView() ?? DEFAULT_CENTER
  );
  const [targetName, setTargetName] = useState<string>("Target 1");
  const [overallEstimatePlaceName, setOverallEstimatePlaceName] = useState<
    string | null
  >(null);
  const [consensusPlaceNames, setConsensusPlaceNames] = useState<
    Record<string, string>
  >({});
  const consensusPlaceNamesRef = useRef<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const popoutContentRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const pendingMessagesRef = useRef<string[]>([]);
  const joinedSessionRef = useRef(joinedSession);
  const callsignDialogModeRef = useRef(callsignDialogMode);
  const pendingCallsignSubmissionRef = useRef<string | null>(
    pendingCallsignSubmission
  );
  const formModeRef = useRef(formMode);
  const selectedMeasurementIdRef = useRef(selectedMeasurementId);
  const targetDraftRef = useRef<SavedTargetDraft | null>(targetDraft);
  const measurementsRef = useRef<Measurement[]>([]);
  const savedTargetsRef = useRef<SavedTarget[]>([]);
  const cursorActiveRef = useRef(false);
  const cursorLastSentAtRef = useRef(0);
  const lastCursorPointRef = useRef<{ lat: number; lon: number } | null>(null);
  const initialMapViewPublishedRef = useRef(false);
  const appOpenedTrackedRef = useRef(false);
  const popoutPointerDownRef = useRef(false);
  const suppressNextMapClickRef = useRef(false);
  const mapDragSuspendedRef = useRef(false);
  const mapDragWasEnabledRef = useRef(false);
  const mapInteractionContainerRef = useRef<HTMLElement | null>(null);
  const popoutSelectionInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null
  );
  const popoutSelectionAnchorRef = useRef<number | null>(null);

  const trackAppEvent = useCallback(
    (eventName: TelemetryEventName, params: TelemetryParams = {}) => {
      trackTelemetry(eventName, {
        ...buildTelemetryContext({ anonymousUserId: userId, sessionId }),
        ...params
      });
    },
    [sessionId, userId]
  );

  const clearDraft = useCallback(() => {
    setDraft(null);
    setFormMode(null);
    setFormError(null);
    setFormNotice(null);
    setEditingVersion(null);
  }, []);

  const clearTargetDraft = useCallback(() => {
    setTargetDraft(null);
    setTargetEditNotice(null);
  }, []);

  useEffect(() => {
    if (!callsign) return;
    saveCallsign(callsign);
  }, [callsign, saveCallsign]);

  useEffect(() => {
    joinedSessionRef.current = joinedSession;
  }, [joinedSession]);

  useEffect(() => {
    callsignDialogModeRef.current = callsignDialogMode;
  }, [callsignDialogMode]);

  useEffect(() => {
    pendingCallsignSubmissionRef.current = pendingCallsignSubmission;
  }, [pendingCallsignSubmission]);

  useEffect(() => {
    formModeRef.current = formMode;
  }, [formMode]);

  useEffect(() => {
    selectedMeasurementIdRef.current = selectedMeasurementId;
  }, [selectedMeasurementId]);

  useEffect(() => {
    targetDraftRef.current = targetDraft;
  }, [targetDraft]);

  useEffect(() => {
    measurementsRef.current = measurements;
  }, [measurements]);

  useEffect(() => {
    savedTargetsRef.current = savedTargets;
  }, [savedTargets]);

  useEffect(() => {
    setTelemetryUserIdentity(userId);
    if (appOpenedTrackedRef.current) return;
    appOpenedTrackedRef.current = true;
    trackTelemetry(TELEMETRY_EVENTS.APP_OPENED, {
      ...buildTelemetryContext({ anonymousUserId: userId, sessionId: null })
    });
  }, [userId]);

  useEffect(() => {
    let active = true;
    const initializeSession = async () => {
      const pathId = window.location.pathname.split("/").filter(Boolean)[0];
      if (pathId && isValidSessionId(pathId)) {
        const normalized = normalizeSessionId(pathId);
        trackTelemetry(TELEMETRY_EVENTS.SESSION_JOIN_REQUESTED, {
          ...buildTelemetryContext({
            anonymousUserId: userId,
            sessionId: normalized
          }),
          source: "path"
        });
        if (normalized !== pathId) {
          window.history.replaceState(null, "", `/${normalized}`);
        }
        if (active) {
          setSessionId(normalized);
        }
        return;
      }
      try {
        setStatusMessage("Creating a shared session...");
        const createdId = await createSession();
        if (!active) return;
        window.history.replaceState(null, "", `/${createdId}`);
        setSessionId(createdId);
        setStatusMessage("Session ready.");
        trackTelemetry(TELEMETRY_EVENTS.SESSION_CREATED, {
          ...buildTelemetryContext({
            anonymousUserId: userId,
            sessionId: createdId
          }),
          result: "success"
        });
      } catch {
        if (!active) return;
        setStatusMessage("Unable to create a shared session.");
        trackTelemetry(TELEMETRY_EVENTS.SESSION_CREATE_FAILED, {
          ...buildTelemetryContext({ anonymousUserId: userId, sessionId: null }),
          result: "failure"
        });
      }
    };
    initializeSession();
    return () => {
      active = false;
    };
  }, [createSession, isValidSessionId, normalizeSessionId, userId]);

  useEffect(() => {
    if (!sessionId) return;
    const cached = loadSession(sessionId);
    const legacy = cached ? null : loadLegacySession();
    const initial = cached ?? legacy;
    const normalizedMeasurements = (initial?.measurements ?? []).map(
      (measurement) => ({
        ...measurement,
        version: measurement.version ?? 1
      })
    );
    const normalizedTargets = (initial?.savedTargets ?? []).map((target) => ({
      ...target,
      version: target.version ?? 1,
      visible: target.visible ?? true
    }));
    setMeasurements(normalizedMeasurements);
    setSavedTargets(
      normalizedTargets.length > 0 ? normalizedTargets : loadLegacyTargets()
    );
    if (initial?.createdAt) {
      setSessionMeta({
        createdAt: initial.createdAt,
        lastEditedAt: initial.lastEditedAt ?? initial.createdAt
      });
    }
  }, [loadLegacySession, loadLegacyTargets, loadSession, sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    saveSession(sessionId, buildSession(measurements, sessionMeta, savedTargets));
  }, [buildSession, measurements, saveSession, savedTargets, sessionId, sessionMeta]);

  const sendMessage = useCallback((payload: Record<string, unknown>) => {
    const raw = JSON.stringify(payload);
    const messageType =
      typeof payload.type === "string" ? payload.type : "";
    const canSendBeforeJoin =
      messageType === "join" ||
      messageType === "preview_presence" ||
      messageType === "request_snapshot";
    const socket = wsRef.current;
    if (
      socket &&
      socket.readyState === WebSocket.OPEN &&
      (joinedSessionRef.current || canSendBeforeJoin)
    ) {
      socket.send(raw);
    } else {
      pendingMessagesRef.current.push(raw);
    }
  }, []);

  const requestSnapshot = useCallback(() => {
    sendMessage({ type: "request_snapshot" });
  }, [sendMessage]);

  const sendEditingPresence = useCallback(
    (entityType: "measurement" | "savedTarget", id: string, active: boolean) => {
      sendMessage({ type: "editing_presence", entityType, id, active });
    },
    [sendMessage]
  );

  const sendCursorPresence = useCallback(
    (update: { active: boolean; lat?: number; lon?: number }) => {
      if (!sessionId) return;
      const socket = wsRef.current;
      if (
        !socket ||
        socket.readyState !== WebSocket.OPEN ||
        !joinedSessionRef.current
      ) {
        return;
      }
      socket.send(
        JSON.stringify({
          type: "cursor_presence",
          sessionId,
          userId,
          active: update.active,
          ...(update.active ? { lat: update.lat, lon: update.lon } : {})
        })
      );
    },
    [sessionId, userId]
  );

  const sendMapViewPresence = useCallback(
    (view: MapViewState) => {
      if (!sessionId) return;
      const socket = wsRef.current;
      if (
        !socket ||
        socket.readyState !== WebSocket.OPEN ||
        !joinedSessionRef.current
      ) {
        return;
      }
      socket.send(
        JSON.stringify({
          type: "map_view_presence",
          sessionId,
          userId,
          lat: view.lat,
          lon: view.lon,
          zoom: view.zoom
        })
      );
    },
    [sessionId, userId]
  );

  const sendCursorInactive = useCallback(() => {
    if (!cursorActiveRef.current) return;
    cursorActiveRef.current = false;
    cursorLastSentAtRef.current = 0;
    lastCursorPointRef.current = null;
    sendCursorPresence({ active: false });
  }, [sendCursorPresence]);

  const handleMapCursorMove = useCallback(
    (latlng: LatLng) => {
      const lat = latlng.lat;
      const lon = latlng.lng;
      const previous = lastCursorPointRef.current;
      const moved =
        !previous ||
        Math.abs(previous.lat - lat) > 0.00001 ||
        Math.abs(previous.lon - lon) > 0.00001;
      if (!moved) return;

      const now = Date.now();
      if (
        cursorActiveRef.current &&
        now - cursorLastSentAtRef.current < CURSOR_THROTTLE_MS
      ) {
        return;
      }

      cursorActiveRef.current = true;
      cursorLastSentAtRef.current = now;
      lastCursorPointRef.current = { lat, lon };
      sendCursorPresence({ active: true, lat, lon });
    },
    [sendCursorPresence]
  );

  const handleMapCursorLeave = useCallback(() => {
    sendCursorInactive();
  }, [sendCursorInactive]);

  const handleMapMove = useCallback(
    (view: MapViewState) => {
      setMapView(view);
      sendMapViewPresence(view);
    },
    [sendMapViewPresence]
  );

  const applyMeasurementUpdate = useCallback(
    (item: Measurement) => {
      setMeasurements((prev) => {
        const existing = prev.find((measurement) => measurement.id === item.id);
        if (existing && item.version < existing.version) {
          requestSnapshot();
          return prev;
        }
        return upsertById(prev, item);
      });
    },
    [requestSnapshot]
  );

  const applySavedTargetUpdate = useCallback(
    (item: SavedTarget) => {
      setSavedTargets((prev) => {
        const existing = prev.find((target) => target.id === item.id);
        if (existing && item.version < existing.version) {
          requestSnapshot();
          return prev;
        }
        return upsertById(prev, item);
      });
    },
    [requestSnapshot]
  );

  const getCallsignValidationError = useCallback(
    (value: string, roster: PresenceUser[]) => {
      const normalized = normalizeCallsign(value);
      if (!normalized) {
        return "Callsign cannot be empty.";
      }
      if (isCallsignTaken(normalized, roster, userId)) {
        return "Callsign already in use.";
      }
      return null;
    },
    [userId]
  );

  const findSelfPresence = useCallback(
    (roster: PresenceUser[]) =>
      roster.find((entry) => entry.userId === userId) ?? null,
    [userId]
  );

  useEffect(() => {
    if (!sessionId) return;
    pendingMessagesRef.current = [];
    let cancelled = false;

    const flushQueuedMessages = (socket: WebSocket) => {
      const queued = [...pendingMessagesRef.current];
      pendingMessagesRef.current = [];
      queued.forEach((message) => {
        socket.send(message);
      });
    };

    const connect = () => {
      if (cancelled) return;
      setCollabStatus("connecting");
      setJoinedSession(false);
      const socket = new WebSocket(getCollabWsUrl());
      wsRef.current = socket;

      socket.onopen = () => {
        reconnectAttemptRef.current = 0;
        setCollabStatus("connected");
        trackAppEvent(TELEMETRY_EVENTS.WS_CONNECTED, {
          reconnect_attempt: 0
        });
        socket.send(
          JSON.stringify({
            type: "preview_presence",
            sessionId
          })
        );
      };

      socket.onmessage = (event) => {
        let payload: Record<string, unknown>;
        try {
          payload = JSON.parse(event.data as string) as Record<string, unknown>;
        } catch {
          return;
        }
        switch (payload.type) {
          case "presence_preview": {
            const preview = (payload.presence as PresenceUser[]) ?? [];
            setPresence(preview);
            if (joinedSessionRef.current) {
              break;
            }
            const stored = loadCallsign();
            const { value, conflict } = resolveInitialCallsignDraft(
              stored,
              preview,
              userId
            );
            const normalized = normalizeCallsign(value);
            setCallsignDraft(normalized);
            setCallsignError(conflict ? "Callsign already in use." : null);
            if (stored && normalizeCallsign(stored) && !conflict && normalized) {
              setCallsignDialogMode(null);
              setCallsignSubmitting(true);
              setPendingCallsignSubmission(normalized);
              trackAppEvent(TELEMETRY_EVENTS.SESSION_JOIN_REQUESTED, {
                source: "stored_callsign_auto_join"
              });
              sendMessage({
                type: "join",
                sessionId,
                userId,
                callsign: normalized
              });
            } else {
              setCallsignDialogMode("join");
              setCallsignSubmitting(false);
              setPendingCallsignSubmission(null);
            }
            break;
          }
          case "snapshot": {
            const session = payload.session as SessionMeta | undefined;
            const roster = (payload.presence as PresenceUser[]) ?? [];
            setSessionMeta(
              session ?? {
                createdAt: new Date().toISOString(),
                lastEditedAt: new Date().toISOString()
              }
            );
            setMeasurements((payload.measurements as Measurement[]) ?? []);
            setSavedTargets((payload.savedTargets as SavedTarget[]) ?? []);
            setPresence(roster);
            setRemoteCursors(
              buildCursorPresenceMap((payload.cursors as CursorPresence[]) ?? [])
            );
            setRemoteMapViews(
              buildMapViewPresenceMap((payload.mapViews as MapViewPresence[]) ?? [])
            );
            setEditingPresence((payload.editing as EditingPresence[]) ?? []);
            clearDraft();
            clearTargetDraft();
            setMapPopout(null);
            setSelectedMeasurementId(null);
            setJoinedSession(true);
            setCallsignSubmitting(false);
            setPendingCallsignSubmission(null);
            setCallsignDialogMode(null);
            setCallsignError(null);
            const self = findSelfPresence(roster);
            if (self) {
              setCallsign(self.callsign);
              setCallsignDraft(self.callsign);
            }
            trackAppEvent(TELEMETRY_EVENTS.SESSION_SNAPSHOT_LOADED, {
              measurement_count: Array.isArray(payload.measurements)
                ? payload.measurements.length
                : 0,
              saved_target_count: Array.isArray(payload.savedTargets)
                ? payload.savedTargets.length
                : 0,
              presence_count: roster.length
            });
            flushQueuedMessages(socket);
            break;
          }
          case "mutation": {
            const entity = payload.entity as string | undefined;
            const action = payload.action as string | undefined;
            setSessionMeta((prev) =>
              prev ? { ...prev, lastEditedAt: new Date().toISOString() } : prev
            );
            if (entity === "measurement") {
              if (action === "delete") {
                const id = payload.id as string;
                setMeasurements((prev) =>
                  prev.filter((measurement) => measurement.id !== id)
                );
              } else if (payload.item) {
                applyMeasurementUpdate(payload.item as Measurement);
              }
            }
            if (entity === "savedTarget") {
              if (action === "delete") {
                const id = payload.id as string;
                setSavedTargets((prev) => prev.filter((target) => target.id !== id));
              } else if (payload.item) {
                applySavedTargetUpdate(payload.item as SavedTarget);
              }
            }
            break;
          }
          case "presence": {
            const roster = (payload.presence as PresenceUser[]) ?? [];
            setPresence(roster);
            setRemoteCursors((prev) =>
              filterCursorPresenceByRoster(
                prev,
                new Set(roster.map((entry) => entry.userId))
              )
            );
            setRemoteMapViews((prev) =>
              filterMapViewPresenceByRoster(
                prev,
                new Set(roster.map((entry) => entry.userId))
              )
            );
            const self = findSelfPresence(roster);
            if (self) {
              setCallsign(self.callsign);
              const pending = pendingCallsignSubmissionRef.current;
              if (
                pending &&
                canonicalizeCallsign(self.callsign) ===
                  canonicalizeCallsign(pending)
              ) {
                setCallsignSubmitting(false);
                setPendingCallsignSubmission(null);
                setCallsignError(null);
                setCallsignDraft(self.callsign);
                if (callsignDialogModeRef.current === "edit") {
                  setCallsignDialogMode(null);
                }
              }
            }
            break;
          }
          case "cursor_presence": {
            const cursorUpdate: CursorPresence = {
              userId:
                typeof payload.userId === "string" ? payload.userId : "",
              callsign:
                typeof payload.callsign === "string" ? payload.callsign : "",
              color: typeof payload.color === "string" ? payload.color : "#2d6cdf",
              active: payload.active !== false,
              lat:
                typeof payload.lat === "number" ? payload.lat : undefined,
              lon:
                typeof payload.lon === "number" ? payload.lon : undefined,
              updatedAt:
                typeof payload.updatedAt === "string"
                  ? payload.updatedAt
                  : new Date().toISOString()
            };
            setRemoteCursors((prev) =>
              applyCursorPresenceUpdate(prev, cursorUpdate)
            );
            break;
          }
          case "map_view_presence": {
            const mapViewUpdate: MapViewPresence = {
              userId:
                typeof payload.userId === "string" ? payload.userId : "",
              lat: typeof payload.lat === "number" ? payload.lat : Number.NaN,
              lon: typeof payload.lon === "number" ? payload.lon : Number.NaN,
              zoom:
                typeof payload.zoom === "number" ? payload.zoom : Number.NaN,
              updatedAt:
                typeof payload.updatedAt === "string"
                  ? payload.updatedAt
                  : new Date().toISOString()
            };
            setRemoteMapViews((prev) =>
              applyMapViewPresenceUpdate(prev, mapViewUpdate)
            );
            break;
          }
          case "editing":
            setEditingPresence((payload.editing as EditingPresence[]) ?? []);
            break;
          case "callsign_conflict": {
            const conflictWith = payload.conflictWith as
              | { callsign?: string }
              | null
              | undefined;
            setCallsignSubmitting(false);
            setPendingCallsignSubmission(null);
            setCallsignError(
              conflictWith?.callsign
                ? `${conflictWith.callsign} is already in use.`
                : "Callsign already in use."
            );
            setCallsignDialogMode(joinedSessionRef.current ? "edit" : "join");
            trackAppEvent(TELEMETRY_EVENTS.CALLSIGN_CONFLICT, {
              result: "error"
            });
            break;
          }
          case "conflict": {
            const entity = payload.entity as string | undefined;
            const current = payload.current as Measurement | SavedTarget | null;
            trackAppEvent(TELEMETRY_EVENTS.MUTATION_CONFLICT, {
              entity: entity ?? "unknown",
              reason:
                typeof payload.reason === "string" ? payload.reason : "unknown"
            });
            if (entity === "measurement") {
              if (current) {
                applyMeasurementUpdate(current as Measurement);
              }
              if (
                formModeRef.current === "edit" &&
                selectedMeasurementIdRef.current === payload.id &&
                current
              ) {
                const measurement = current as Measurement;
                setDraft({
                  lat: measurement.lat,
                  lon: measurement.lon,
                  type: measurement.type,
                  value: measurement.value,
                  units: measurement.units,
                  northReference: measurement.northReference,
                  declination: measurement.declination,
                  distanceErrorPct: measurement.distanceErrorPct,
                  bearingErrorDeg: measurement.bearingErrorDeg
                });
                setEditingVersion(measurement.version);
                setFormNotice("Updated by someone else. Draft refreshed.");
              } else {
                setStatusMessage("Update conflict. Please retry.");
              }
            }
            if (entity === "savedTarget") {
              if (current) {
                applySavedTargetUpdate(current as SavedTarget);
              }
              if (
                targetDraftRef.current &&
                payload.id === targetDraftRef.current.id &&
                current
              ) {
                const target = current as SavedTarget;
                setTargetDraft({
                  id: target.id,
                  version: target.version,
                  name: target.name,
                  lat: target.lat,
                  lon: target.lon,
                  visible: target.visible
                });
                setTargetEditNotice("Updated by someone else. Draft refreshed.");
              } else {
                setStatusMessage("Update conflict. Please retry.");
              }
            }
            break;
          }
          case "error": {
            const message = payload.message as string | undefined;
            if (message) setStatusMessage(message);
            if (message) {
              trackAppEvent(TELEMETRY_EVENTS.SERVER_ERROR, {
                error_kind: classifyServerMessage(message)
              });
            }
            break;
          }
          default:
            break;
        }
      };

      socket.onclose = () => {
        if (cancelled) return;
        setCollabStatus("disconnected");
        setJoinedSession(false);
        setRemoteCursors({});
        setRemoteMapViews({});
        setCallsignSubmitting(false);
        cursorActiveRef.current = false;
        cursorLastSentAtRef.current = 0;
        lastCursorPointRef.current = null;
        const attempt = reconnectAttemptRef.current;
        const delay = Math.min(12_000, 800 * 1.6 ** attempt);
        trackAppEvent(TELEMETRY_EVENTS.WS_DISCONNECTED, {
          reconnect_attempt: attempt
        });
        trackAppEvent(TELEMETRY_EVENTS.WS_RECONNECT_SCHEDULED, {
          reconnect_attempt: attempt,
          reconnect_delay_ms: delay
        });
        reconnectAttemptRef.current += 1;
        if (reconnectTimerRef.current) {
          window.clearTimeout(reconnectTimerRef.current);
        }
        reconnectTimerRef.current = window.setTimeout(() => {
          connect();
        }, delay);
      };

      socket.onerror = () => {
        socket.close();
      };
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
      }
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [
    applyMeasurementUpdate,
    applySavedTargetUpdate,
    clearDraft,
    clearTargetDraft,
    findSelfPresence,
    sendMessage,
    sessionId,
    trackAppEvent,
    userId
  ]);

  useEffect(() => {
    const handleHidden = () => {
      if (document.visibilityState === "hidden") {
        sendCursorInactive();
      }
    };
    const handleBlur = () => {
      sendCursorInactive();
    };
    document.addEventListener("visibilitychange", handleHidden);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleHidden);
      window.removeEventListener("blur", handleBlur);
    };
  }, [sendCursorInactive]);

  useEffect(() => {
    saveMapView(mapView);
  }, [mapView, saveMapView]);

  useEffect(() => {
    if (joinedSession && collabStatus === "connected") {
      if (!initialMapViewPublishedRef.current) {
        initialMapViewPublishedRef.current = true;
        sendMapViewPresence(mapView);
      }
      return;
    }
    initialMapViewPublishedRef.current = false;
  }, [collabStatus, joinedSession, mapView, sendMapViewPresence]);

  useEffect(() => {
    if (!simulateRandomCursors || !mapInstance) {
      setSimulatedCursors([]);
      return;
    }

    const updateSimulatedCursors = () => {
      const updatedAt = new Date().toISOString();
      const next = simulatedCursorProfiles.slice(0, SIMULATED_CURSOR_COUNT).map((profile) => {
        const point = randomPointInMapBounds(mapInstance);
        return {
          userId: profile.userId,
          callsign: profile.callsign,
          color: profile.color,
          active: true,
          lat: point.lat,
          lon: point.lon,
          updatedAt
        } as CursorPresence;
      });
      setSimulatedCursors(next);
    };

    updateSimulatedCursors();
    const interval = window.setInterval(
      updateSimulatedCursors,
      SIMULATED_CURSOR_INTERVAL_MS
    );

    return () => {
      window.clearInterval(interval);
      setSimulatedCursors([]);
    };
  }, [mapInstance, simulateRandomCursors]);

  useEffect(() => {
    consensusPlaceNamesRef.current = consensusPlaceNames;
  }, [consensusPlaceNames]);

  useEffect(() => {
    const node = popoutContentRef.current;
    if (!node) return;
    L.DomEvent.disableClickPropagation(node);
    L.DomEvent.disableScrollPropagation(node);
    const popup = node.closest(".leaflet-popup") as HTMLElement | null;
    if (popup) {
      L.DomEvent.disableClickPropagation(popup);
      L.DomEvent.disableScrollPropagation(popup);
    }
  }, [mapPopout]);

  const setMapDragSuspended = useCallback(
    (active: boolean) => {
      const map = mapInstance;
      const container = map?.getContainer() ?? mapInteractionContainerRef.current;
      if (active) {
        if (mapDragSuspendedRef.current) return;
        mapDragSuspendedRef.current = true;
        if (container instanceof HTMLElement) {
          container.classList.add("popup-input-drag-active");
          mapInteractionContainerRef.current = container;
        }
        document.body.classList.add("popup-input-drag-active");
        if (map) {
          const wasEnabled = map.dragging.enabled();
          mapDragWasEnabledRef.current = wasEnabled;
          if (wasEnabled) map.dragging.disable();
        }
        return;
      }

      if (map && mapDragSuspendedRef.current && mapDragWasEnabledRef.current) {
        map.dragging.enable();
      }
      if (container instanceof HTMLElement) {
        container.classList.remove("popup-input-drag-active");
      }
      document.body.classList.remove("popup-input-drag-active");
      mapInteractionContainerRef.current = null;
      mapDragSuspendedRef.current = false;
      mapDragWasEnabledRef.current = false;
    },
    [mapInstance]
  );

  useEffect(() => {
    return () => {
      setMapDragSuspended(false);
    };
  }, [setMapDragSuspended]);

  useEffect(() => {
    const applyOutsideInputSelection = (event: PointerEvent) => {
      if (!popoutPointerDownRef.current) return;
      const input = popoutSelectionInputRef.current;
      const anchor = popoutSelectionAnchorRef.current;
      if (!input || anchor === null) return;
      const rect = input.getBoundingClientRect();
      const nextIndex =
        event.clientX < rect.left
          ? 0
          : event.clientX > rect.right
            ? input.value.length
            : null;
      if (nextIndex === null) return;
      const start = Math.min(anchor, nextIndex);
      const end = Math.max(anchor, nextIndex);
      const direction: "forward" | "backward" =
        nextIndex < anchor ? "backward" : "forward";
      event.preventDefault();
      window.requestAnimationFrame(() => {
        try {
          if (document.activeElement !== input) {
            input.focus({ preventScroll: true });
          }
          input.setSelectionRange(start, end, direction);
        } catch {
          // Ignore unsupported selection operations.
        }
      });
    };

    const completePopoutPointerInteraction = (target: EventTarget | null) => {
      if (!popoutPointerDownRef.current) return;
      if (!isEventTargetInsideLeafletPopup(target)) {
        suppressNextMapClickRef.current = true;
      }
      popoutPointerDownRef.current = false;
      popoutSelectionInputRef.current = null;
      popoutSelectionAnchorRef.current = null;
      setMapDragSuspended(false);
    };
    const clearPointerTracking = () => {
      popoutPointerDownRef.current = false;
      popoutSelectionInputRef.current = null;
      popoutSelectionAnchorRef.current = null;
      setMapDragSuspended(false);
    };
    const handlePointerMove = (event: PointerEvent) => {
      applyOutsideInputSelection(event);
    };
    const handlePointerUp = (event: PointerEvent) => {
      completePopoutPointerInteraction(event.target);
    };
    window.addEventListener("pointermove", handlePointerMove, {
      capture: true,
      passive: false
    });
    window.addEventListener("pointerup", handlePointerUp, true);
    window.addEventListener("pointercancel", clearPointerTracking, true);
    window.addEventListener("blur", clearPointerTracking);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove, {
        capture: true
      });
      window.removeEventListener("pointerup", handlePointerUp, true);
      window.removeEventListener("pointercancel", clearPointerTracking, true);
      window.removeEventListener("blur", clearPointerTracking);
      popoutSelectionInputRef.current = null;
      popoutSelectionAnchorRef.current = null;
      setMapDragSuspended(false);
    };
  }, [setMapDragSuspended]);

  useEffect(() => {
    if (savedTargets.length === 0) {
      setTargetName("Target 1");
    } else {
      setTargetName(`Target ${savedTargets.length + 1}`);
    }
  }, [savedTargets.length]);

  useEffect(() => {
    if (formMode !== "edit" || !selectedMeasurementId) return;
    const measurement = measurements.find(
      (item) => item.id === selectedMeasurementId
    );
    if (!measurement) {
      clearDraft();
      setStatusMessage("Measurement removed by someone else.");
      return;
    }
    if (editingVersion !== null && measurement.version !== editingVersion) {
      setDraft({
        lat: measurement.lat,
        lon: measurement.lon,
        type: measurement.type,
        value: measurement.value,
        units: measurement.units,
        northReference: measurement.northReference,
        declination: measurement.declination,
        distanceErrorPct: measurement.distanceErrorPct,
        bearingErrorDeg: measurement.bearingErrorDeg
      });
      setEditingVersion(measurement.version);
      setFormNotice("Updated by someone else. Draft refreshed.");
    }
  }, [clearDraft, editingVersion, formMode, measurements, selectedMeasurementId]);

  useEffect(() => {
    if (!targetDraft) return;
    const target = savedTargets.find((item) => item.id === targetDraft.id);
    if (!target) {
      clearTargetDraft();
      setStatusMessage("Target removed by someone else.");
      return;
    }
    if (target.version !== targetDraft.version) {
      setTargetDraft({
        id: target.id,
        version: target.version,
        name: target.name,
        lat: target.lat,
        lon: target.lon,
        visible: target.visible
      });
      setTargetEditNotice("Updated by someone else. Draft refreshed.");
    }
  }, [clearTargetDraft, savedTargets, targetDraft]);

  useEffect(() => {
    if (mapPopout?.kind !== "measurement" || mapPopout.mode !== "edit") return;
    const measurementId = mapPopout.id;
    sendEditingPresence("measurement", measurementId, true);
    const interval = window.setInterval(() => {
      sendEditingPresence("measurement", measurementId, true);
    }, 15_000);
    return () => {
      window.clearInterval(interval);
      sendEditingPresence("measurement", measurementId, false);
    };
  }, [
    mapPopout?.kind === "measurement" ? mapPopout.id : null,
    mapPopout?.kind,
    mapPopout?.mode,
    sendEditingPresence
  ]);

  useEffect(() => {
    if (!targetDraft) return;
    const targetId = targetDraft.id;
    sendEditingPresence("savedTarget", targetId, true);
    const interval = window.setInterval(() => {
      sendEditingPresence("savedTarget", targetId, true);
    }, 15_000);
    return () => {
      window.clearInterval(interval);
      sendEditingPresence("savedTarget", targetId, false);
    };
  }, [sendEditingPresence, targetDraft?.id]);

  const shareLink = useMemo(
    () => (sessionId ? `${window.location.origin}/${sessionId}` : ""),
    [sessionId]
  );

  const editingLookup = useMemo(() => {
    const map = new Map<string, EditingPresence[]>();
    editingPresence.forEach((entry) => {
      const key = `${entry.entityType}:${entry.id}`;
      const list = map.get(key);
      if (list) {
        list.push(entry);
      } else {
        map.set(key, [entry]);
      }
    });
    return map;
  }, [editingPresence]);

  const getEditors = (entityType: "measurement" | "savedTarget", id: string) =>
    (editingLookup.get(`${entityType}:${id}`) ?? []).filter(
      (entry) => entry.userId !== userId
    );

  const realRemoteCursors = useMemo(
    () =>
      Object.values(remoteCursors).filter((cursor) => cursor.userId !== userId),
    [remoteCursors, userId]
  );

  const simulatedParticipantIds = useMemo(
    () => simulatedCursors.map((cursor) => cursor.userId),
    [simulatedCursors]
  );

  const visibleRemoteCursors = useMemo(() => {
    if (!simulateRandomCursors) {
      return realRemoteCursors;
    }
    return [...realRemoteCursors, ...simulatedCursors];
  }, [realRemoteCursors, simulateRandomCursors, simulatedCursors]);

  const remoteMapViewEntries = useMemo(
    () => Object.values(remoteMapViews),
    [remoteMapViews]
  );

  const measurementGeometries = useMemo(() => {
    return measurements.map((measurement) => measurementToGeometry(measurement));
  }, [measurements]);

  const geometryItems = useMemo(
    () =>
      measurementGeometries
        .map((geometry, index) =>
          geometry?.polygon
            ? {
                measurement: measurements[index],
                polygon: geometry.polygon
              }
            : null
        )
        .filter(
          (
            item
          ): item is {
            measurement: Measurement;
            polygon: Feature<Polygon | MultiPolygon>;
          } => Boolean(item)
        ),
    [measurementGeometries, measurements]
  );

  const geometryKeySeed = useMemo(
    () => geometryKeySeedFromMeasurements(measurements),
    [measurements]
  );

  const confidence = useMemo(() => {
    const polygons = measurementGeometries
      .map((item) => item?.polygon)
      .filter((polygon): polygon is NonNullable<typeof polygon> => Boolean(polygon));
    return computeConfidenceRegions(polygons);
  }, [measurementGeometries]);

  const distanceMeasurements = useMemo(
    () => measurements.filter((measurement) => measurement.type === "distance"),
    [measurements]
  );
  const hasSingleBearingOnly = useMemo(
    () => measurements.length === 1 && measurements[0].type === "bearing",
    [measurements]
  );
  const hasSingleDistanceOnlyWithUncertainty = useMemo(
    () => hasSingleDistanceUncertainty(measurements),
    [measurements]
  );
  const hasDistanceUncertainty = useMemo(
    () =>
      distanceMeasurements.some(
        (measurement) => (measurement.distanceErrorPct ?? 0) > 0
      ),
    [distanceMeasurements]
  );
  const isTwoDistanceOnly =
    measurements.length === 2 &&
    distanceMeasurements.length === 2 &&
    !hasDistanceUncertainty;

  const distanceCandidates = useMemo(() => {
    if (!isTwoDistanceOnly) return [];
    return circleIntersections(distanceMeasurements[0], distanceMeasurements[1]);
  }, [distanceMeasurements, isTwoDistanceOnly]);
  const overlapCandidates = useMemo(() => {
    if (measurements.length !== 2 || isTwoDistanceOnly || !confidence.all) return [];
    return estimateCenters(confidence.all);
  }, [confidence.all, isTwoDistanceOnly, measurements.length]);
  const hasTwoMeasurementAmbiguity =
    measurements.length === 2 &&
    !isTwoDistanceOnly &&
    overlapCandidates.length > 1;
  const hasTwoMeasurementConflict =
    measurements.length === 2 &&
    !hasSingleBearingOnly &&
    !isTwoDistanceOnly &&
    !confidence.all;
  const candidateEstimates = useMemo(() => {
    if (isTwoDistanceOnly) return distanceCandidates;
    if (hasTwoMeasurementAmbiguity) return overlapCandidates;
    return [];
  }, [
    distanceCandidates,
    hasTwoMeasurementAmbiguity,
    isTwoDistanceOnly,
    overlapCandidates
  ]);

  const hasMultiple = measurements.length > 1;
  const hasMany = measurements.length > 2;
  const confidenceDisplay = useMemo(
    () => ({
      all: isTwoDistanceOnly ? null : hasMultiple ? confidence.all : null,
      allButOne: isTwoDistanceOnly ? null : hasMany ? confidence.allButOne : null,
      union: isTwoDistanceOnly ? null : confidence.union
    }),
    [confidence, hasMany, hasMultiple, isTwoDistanceOnly]
  );

  const consensusEstimates = useMemo(() => {
    if (isTwoDistanceOnly) return [];
    if (geometryItems.length < 3) return [];
    if (confidence.all) return [];
    return computeConsensusEstimates(geometryItems, 3);
  }, [confidence.all, geometryItems, isTwoDistanceOnly]);

  const overallEstimate = useMemo(() => {
    if (isTwoDistanceOnly) return null;
    if (hasSingleBearingOnly) return null;
    if (hasSingleDistanceOnlyWithUncertainty) return null;
    if (hasTwoMeasurementAmbiguity) return null;
    if (measurements.length === 2 && !confidence.all) return null;
    if (confidence.all) {
      return estimateCenter(confidence.all);
    }
    if (consensusEstimates.length > 0) {
      const avg = consensusEstimates.reduce(
        (acc, candidate) => ({
          lat: acc.lat + candidate.lat,
          lon: acc.lon + candidate.lon
        }),
        { lat: 0, lon: 0 }
      );
      return {
        lat: avg.lat / consensusEstimates.length,
        lon: avg.lon / consensusEstimates.length
      };
    }
    if (confidence.union) {
      return estimateCenter(confidence.union);
    }
    if (geometryItems[0]) {
      return estimateCenter(geometryItems[0].polygon);
    }
    return null;
  }, [
    confidence.all,
    confidence.union,
    consensusEstimates,
    geometryItems,
    hasSingleDistanceOnlyWithUncertainty,
    hasTwoMeasurementAmbiguity,
    hasSingleBearingOnly,
    isTwoDistanceOnly,
    measurements.length
  ]);

  useEffect(() => {
    setOverallEstimatePlaceName(null);
    if (!overallEstimate || !geocodeEnabled) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    reverseGeocode(overallEstimate.lat, overallEstimate.lon, controller.signal)
      .then((placeName) => {
        if (placeName) setOverallEstimatePlaceName(placeName);
      })
      .catch(() => {
        // Ignore failures for estimate geocoding.
      })
      .finally(() => {
        clearTimeout(timeout);
      });
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [geocodeEnabled, overallEstimate?.lat, overallEstimate?.lon, reverseGeocode]);

  useEffect(() => {
    if (!geocodeEnabled) return;
    if (consensusEstimates.length === 0) {
      setConsensusPlaceNames({});
      return;
    }
    const activeIds = new Set(consensusEstimates.map((candidate) => candidate.id));
    setConsensusPlaceNames((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([id, name]) => {
        if (activeIds.has(id)) next[id] = name;
      });
      return next;
    });

    let cancelled = false;
    const controllers: AbortController[] = [];

    consensusEstimates.forEach((candidate) => {
      if (consensusPlaceNamesRef.current[candidate.id]) return;
      const controller = new AbortController();
      controllers.push(controller);
      const timeout = setTimeout(() => controller.abort(), 8000);
      reverseGeocode(candidate.lat, candidate.lon, controller.signal)
        .then((placeName) => {
          if (cancelled || !placeName) return;
          setConsensusPlaceNames((prev) =>
            prev[candidate.id] ? prev : { ...prev, [candidate.id]: placeName }
          );
        })
        .catch(() => {
          // Ignore failures for consensus geocoding.
        })
        .finally(() => {
          clearTimeout(timeout);
        });
    });

    return () => {
      cancelled = true;
      controllers.forEach((controller) => controller.abort());
    };
  }, [consensusEstimates, geocodeEnabled, reverseGeocode]);

  const [candidateFocus, setCandidateFocus] = useState<{
    lat: number;
    lon: number;
  } | null>(null);

  const focusPoint = useMemo(() => {
    if (candidateFocus) return candidateFocus;
    if (selectedMeasurementId) {
      const found = measurements.find((m) => m.id === selectedMeasurementId);
      if (found) return { lat: found.lat, lon: found.lon };
    }
    return null;
  }, [candidateFocus, measurements, selectedMeasurementId]);

  const applyFitActionToMap = useCallback(
    (map: L.Map, action: ReturnType<typeof getPointFitAction>) => {
      if (action.kind === "noop") {
        return;
      }

      if (action.kind === "center") {
        map.setView([action.point.lat, action.point.lon], map.getZoom(), {
          animate: true
        });
        return;
      }

      if (action.kind === "applyRemoteView") {
        map.setView([action.view.lat, action.view.lon], action.view.zoom, {
          animate: true
        });
        return;
      }

      const bounds = L.latLngBounds(
        action.points.map((point) => [point.lat, point.lon] as [number, number])
      );
      const size = map.getSize();
      const padX = Math.max(24, Math.round(size.x * FIT_PADDING_RATIO));
      const padY = Math.max(24, Math.round(size.y * FIT_PADDING_RATIO));
      map.fitBounds(bounds, {
        padding: [padX, padY],
        animate: true
      });
    },
    []
  );

  const handleZoomToFitPoints = useCallback(
    (map: L.Map) => {
      trackAppEvent(TELEMETRY_EVENTS.ZOOM_FIT_POINTS_CLICKED, {
        measurement_count: measurements.length,
        saved_target_count: savedTargets.length
      });
      const points = collectPointsForFit({
        measurements,
        savedTargets,
        overallEstimate,
        distanceCandidates: candidateEstimates,
        consensusEstimates: consensusEstimates.map((item) => ({
          lat: item.lat,
          lon: item.lon
        }))
      });
      applyFitActionToMap(map, getPointFitAction(points));
    },
    [
      applyFitActionToMap,
      candidateEstimates,
      consensusEstimates,
      measurements,
      overallEstimate,
      savedTargets,
      trackAppEvent
    ]
  );

  const handleZoomToFitUsers = useCallback(
    (map: L.Map) => {
      trackAppEvent(TELEMETRY_EVENTS.ZOOM_FIT_USERS_CLICKED, {
        presence_count: presence.length,
        remote_cursor_count: visibleRemoteCursors.length
      });
      const action = getUserFitAction({
        localUserId: userId,
        presenceUserIds: presence.map((entry) => entry.userId),
        cursorEntries: visibleRemoteCursors,
        mapViewEntries: remoteMapViewEntries,
        extraParticipantIds: simulateRandomCursors ? simulatedParticipantIds : []
      });
      applyFitActionToMap(map, action);
    },
    [
      applyFitActionToMap,
      presence,
      remoteMapViewEntries,
      simulateRandomCursors,
      simulatedParticipantIds,
      trackAppEvent,
      userId,
      visibleRemoteCursors
    ]
  );

  const startDraftAt = (lat: number, lon: number) => {
    setDraft(emptyDraftFromPoint(lat, lon));
    setFormMode(null);
    setSelectedMeasurementId(null);
    setCandidateFocus(null);
    setFormError(null);
    setMapPopout({ kind: "draft", lat, lon, mode: "menu" });
    setStatusMessage("Choose how to use this point.");
  };

  const openMeasurementView = (measurement: Measurement) => {
    setSelectedMeasurementId(measurement.id);
    setCandidateFocus(null);
    clearDraft();
    setMapPopout({ kind: "measurement", id: measurement.id, mode: "view" });
  };

  const openMeasurementEdit = (measurement: Measurement) => {
    setSelectedMeasurementId(measurement.id);
    setCandidateFocus(null);
    setDraft({
      lat: measurement.lat,
      lon: measurement.lon,
      type: measurement.type,
      value: measurement.value,
      units: measurement.units,
      northReference: measurement.northReference,
      declination: measurement.declination,
      distanceErrorPct: measurement.distanceErrorPct,
      bearingErrorDeg: measurement.bearingErrorDeg
    });
    setFormMode("edit");
    setFormError(null);
    setFormNotice(null);
    setEditingVersion(measurement.version);
    setMapPopout({ kind: "measurement", id: measurement.id, mode: "edit" });
  };

  const handleMapClick = (latlng: LatLng) => {
    if (suppressNextMapClickRef.current) {
      suppressNextMapClickRef.current = false;
      return;
    }
    startDraftAt(latlng.lat, latlng.lng);
  };

  const handleUseLocation = () => {
    trackAppEvent(TELEMETRY_EVENTS.GEOLOCATION_REQUESTED, {
      result: "requested"
    });
    if (!navigator.geolocation) {
      setStatusMessage("Geolocation is not supported in this browser.");
      trackAppEvent(TELEMETRY_EVENTS.GEOLOCATION_FAILED, {
        error_kind: "unsupported"
      });
      return;
    }
    setStatusMessage("Requesting current location...");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        startDraftAt(latitude, longitude);
        setMapView((prev) => ({ ...prev, lat: latitude, lon: longitude, zoom: 13 }));
        setStatusMessage("Location captured. Choose how to use this point.");
        trackAppEvent(TELEMETRY_EVENTS.GEOLOCATION_SUCCEEDED, {
          result: "success"
        });
      },
      (error) => {
        setStatusMessage(`Location error: ${error.message}`);
        trackAppEvent(TELEMETRY_EVENTS.GEOLOCATION_FAILED, {
          error_kind:
            error.code === error.PERMISSION_DENIED
              ? "permission_denied"
              : error.code === error.TIMEOUT
                ? "timeout"
                : "location_error"
        });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const updateDraft = (updates: Partial<MeasurementDraft>) => {
    if (!draft) return;
    setDraft({ ...draft, ...updates });
  };

  const handleDraftAddMeasurement = () => {
    if (!draft) return;
    setFormMode("add");
    setFormError(null);
    setMapPopout((prev) =>
      prev?.kind === "draft" ? { ...prev, mode: "add" } : prev
    );
    setStatusMessage("Add measurement details.");
  };

  const handleDraftAddPoint = () => {
    if (!draft) return;
    handleSaveTargetAt(draft.lat, draft.lon);
    clearDraft();
    setMapPopout(null);
  };

  const handleCancelDraft = () => {
    clearDraft();
    setMapPopout(null);
  };

  const handleCancelEdit = () => {
    clearDraft();
    if (selectedMeasurementId) {
      setMapPopout({ kind: "measurement", id: selectedMeasurementId, mode: "view" });
    } else {
      setMapPopout(null);
    }
  };

  const handlePopoutClose = () => {
    clearDraft();
    setMapPopout(null);
  };

  const stopPopoutPropagation = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const handlePopoutPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target;
    if (isTextSelectableInputTarget(target)) {
      popoutPointerDownRef.current = true;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        popoutSelectionInputRef.current = target;
        popoutSelectionAnchorRef.current = target.selectionStart ?? 0;
        window.requestAnimationFrame(() => {
          if (
            popoutPointerDownRef.current &&
            popoutSelectionInputRef.current === target &&
            target.selectionStart !== null
          ) {
            popoutSelectionAnchorRef.current = target.selectionStart;
          }
        });
      } else {
        popoutSelectionInputRef.current = null;
        popoutSelectionAnchorRef.current = null;
      }
      setMapDragSuspended(true);
    }
    stopPopoutPropagation(event);
  };

  const validateDraft = () => {
    if (!draft) return "No measurement to save.";
    if (draft.type === "bearing") {
      if (Number.isNaN(draft.value) || draft.value < 0 || draft.value > 360) {
        return "Bearing must be between 0 and 360 degrees.";
      }
      if (
        draft.bearingErrorDeg !== undefined &&
        (Number.isNaN(draft.bearingErrorDeg) ||
          draft.bearingErrorDeg < 0 ||
          draft.bearingErrorDeg > 180)
      ) {
        return "Bearing uncertainty must be between 0 and 180 degrees.";
      }
    }
    if (draft.type === "distance") {
      if (Number.isNaN(draft.value) || draft.value <= 0) {
        return "Distance must be a positive value.";
      }
      if (
        draft.distanceErrorPct !== undefined &&
        (Number.isNaN(draft.distanceErrorPct) ||
          draft.distanceErrorPct < 0 ||
          draft.distanceErrorPct > 100)
      ) {
        return "Distance margin of error must be between 0 and 100%.";
      }
    }
    return null;
  };

  const applyGeocode = async (
    id: string,
    lat: number,
    lon: number,
    expectedVersionFallback?: number
  ) => {
    const plusCode = toPlusCode(lat, lon);
    if (plusCode) {
      trackAppEvent(TELEMETRY_EVENTS.PLUS_CODE_ASSIGNED, {
        entity: "measurement",
        result: "success"
      });
      const measurement =
        measurementsRef.current.find((item) => item.id === id) ??
        (expectedVersionFallback !== undefined
          ? { version: expectedVersionFallback }
          : null);
      if (measurement) {
        sendMessage({
          type: "mutation",
          entity: "measurement",
          action: "update",
          id,
          expectedVersion: measurement.version,
          updates: { plusCode }
        });
        setMeasurements((prev) =>
          prev.map((item) =>
            item.id === id
              ? { ...item, plusCode, version: item.version + 1 }
              : item
          )
        );
      }
    }

    if (!geocodeEnabled) {
      trackAppEvent(TELEMETRY_EVENTS.GEOCODE_LOOKUP_RESULT, {
        entity: "measurement",
        result: "skipped_disabled"
      });
      return;
    }
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      const placeName = await reverseGeocode(lat, lon, controller.signal);
      clearTimeout(timeout);
      if (!placeName) {
        trackAppEvent(TELEMETRY_EVENTS.GEOCODE_LOOKUP_RESULT, {
          entity: "measurement",
          result: "empty"
        });
        return;
      }
      const measurement =
        measurementsRef.current.find((item) => item.id === id) ??
        (expectedVersionFallback !== undefined
          ? { version: expectedVersionFallback }
          : null);
      if (!measurement) return;
      sendMessage({
        type: "mutation",
        entity: "measurement",
        action: "update",
        id,
        expectedVersion: measurement.version,
        updates: { placeName }
      });
      setMeasurements((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, placeName, version: item.version + 1 }
            : item
        )
      );
      trackAppEvent(TELEMETRY_EVENTS.GEOCODE_LOOKUP_RESULT, {
        entity: "measurement",
        result: "success"
      });
    } catch (error) {
      trackAppEvent(TELEMETRY_EVENTS.GEOCODE_LOOKUP_RESULT, {
        entity: "measurement",
        result: "failure",
        error_kind: classifyLookupError(error)
      });
      // Ignore geocode failures, keep core session data.
    }
  };

  const applyTargetGeocode = async (
    id: string,
    lat: number,
    lon: number,
    expectedVersionFallback?: number
  ) => {
    const plusCode = toPlusCode(lat, lon);
    if (plusCode) {
      trackAppEvent(TELEMETRY_EVENTS.PLUS_CODE_ASSIGNED, {
        entity: "saved_target",
        result: "success"
      });
      const target =
        savedTargetsRef.current.find((item) => item.id === id) ??
        (expectedVersionFallback !== undefined
          ? { version: expectedVersionFallback }
          : null);
      if (target) {
        sendMessage({
          type: "mutation",
          entity: "savedTarget",
          action: "update",
          id,
          expectedVersion: target.version,
          updates: { plusCode }
        });
        setSavedTargets((prev) =>
          prev.map((item) =>
            item.id === id
              ? { ...item, plusCode, version: item.version + 1 }
              : item
          )
        );
      }
    }
    if (!geocodeEnabled) {
      trackAppEvent(TELEMETRY_EVENTS.GEOCODE_LOOKUP_RESULT, {
        entity: "saved_target",
        result: "skipped_disabled"
      });
      return;
    }
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      const placeName = await reverseGeocode(lat, lon, controller.signal);
      clearTimeout(timeout);
      if (!placeName) {
        trackAppEvent(TELEMETRY_EVENTS.GEOCODE_LOOKUP_RESULT, {
          entity: "saved_target",
          result: "empty"
        });
        return;
      }
      const target =
        savedTargetsRef.current.find((item) => item.id === id) ??
        (expectedVersionFallback !== undefined
          ? { version: expectedVersionFallback }
          : null);
      if (!target) return;
      sendMessage({
        type: "mutation",
        entity: "savedTarget",
        action: "update",
        id,
        expectedVersion: target.version,
        updates: { placeName }
      });
      setSavedTargets((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, placeName, version: item.version + 1 }
            : item
        )
      );
      trackAppEvent(TELEMETRY_EVENTS.GEOCODE_LOOKUP_RESULT, {
        entity: "saved_target",
        result: "success"
      });
    } catch (error) {
      trackAppEvent(TELEMETRY_EVENTS.GEOCODE_LOOKUP_RESULT, {
        entity: "saved_target",
        result: "failure",
        error_kind: classifyLookupError(error)
      });
      // Ignore failures, keep target data.
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateDraft();
    if (error) {
      setFormError(error);
      return;
    }

    if (!draft) return;

    if (formMode === "add") {
      const id = crypto.randomUUID();
      const createdAt = new Date().toISOString();
      const distanceErrorPct =
        draft.distanceErrorPct !== undefined && draft.distanceErrorPct > 0
          ? draft.distanceErrorPct
          : undefined;
      const bearingErrorDeg =
        draft.bearingErrorDeg !== undefined && draft.bearingErrorDeg > 0
          ? draft.bearingErrorDeg
          : undefined;
      const measurement: Measurement = {
        id,
        version: 1,
        type: draft.type,
        lat: draft.lat,
        lon: draft.lon,
        value: draft.value,
        units: draft.units,
        northReference: draft.northReference,
        declination: draft.declination,
        ...(draft.type === "distance" && distanceErrorPct
          ? { distanceErrorPct }
          : {}),
        ...(draft.type === "bearing" && bearingErrorDeg
          ? { bearingErrorDeg }
          : {}),
        createdAt
      };

      sendMessage({
        type: "mutation",
        entity: "measurement",
        action: "create",
        item: measurement
      });
      setMeasurements((prev) => upsertById(prev, measurement));
      setDraft(null);
      setFormMode(null);
      setFormError(null);
      setFormNotice(null);
      setSelectedMeasurementId(id);
      setMapPopout({ kind: "measurement", id, mode: "view" });
      setStatusMessage("Measurement added.");
      trackAppEvent(TELEMETRY_EVENTS.MEASUREMENT_CREATED, {
        measurement_type: measurement.type,
        has_uncertainty:
          measurement.type === "distance"
            ? typeof measurement.distanceErrorPct === "number"
            : typeof measurement.bearingErrorDeg === "number"
      });
      trackAppEvent(TELEMETRY_EVENTS.SESSION_UPDATED_LOCAL, {
        update_source: "measurement_create"
      });
      await applyGeocode(id, measurement.lat, measurement.lon, measurement.version);
      return;
    }

    if (formMode === "edit" && selectedMeasurementId) {
      const existing = measurementsRef.current.find(
        (measurement) => measurement.id === selectedMeasurementId
      );
      if (!existing) {
        setStatusMessage("Measurement no longer exists.");
        return;
      }
      const distanceErrorPct =
        draft.distanceErrorPct !== undefined && draft.distanceErrorPct > 0
          ? draft.distanceErrorPct
          : undefined;
      const bearingErrorDeg =
        draft.bearingErrorDeg !== undefined && draft.bearingErrorDeg > 0
          ? draft.bearingErrorDeg
          : undefined;
      const updates = {
        type: draft.type,
        value: draft.value,
        units: draft.units,
        northReference: draft.northReference,
        declination: draft.declination,
        distanceErrorPct: draft.type === "distance" ? distanceErrorPct : undefined,
        bearingErrorDeg: draft.type === "bearing" ? bearingErrorDeg : undefined
      };
      sendMessage({
        type: "mutation",
        entity: "measurement",
        action: "update",
        id: selectedMeasurementId,
        expectedVersion: existing.version,
        updates
      });
      setMeasurements((prev) =>
        prev.map((measurement) =>
          measurement.id === selectedMeasurementId
            ? { ...measurement, ...updates, version: measurement.version + 1 }
            : measurement
        )
      );
      setDraft(null);
      setFormMode(null);
      setFormError(null);
      setFormNotice(null);
      setEditingVersion(null);
      setMapPopout({
        kind: "measurement",
        id: selectedMeasurementId,
        mode: "view"
      });
      setStatusMessage("Measurement updated.");
      trackAppEvent(TELEMETRY_EVENTS.MEASUREMENT_UPDATED, {
        measurement_type: updates.type,
        has_uncertainty:
          updates.type === "distance"
            ? typeof updates.distanceErrorPct === "number"
            : typeof updates.bearingErrorDeg === "number"
      });
      trackAppEvent(TELEMETRY_EVENTS.SESSION_UPDATED_LOCAL, {
        update_source: "measurement_update"
      });
    }
  };

  const handleEdit = (measurement: Measurement) => {
    openMeasurementEdit(measurement);
  };

  const handleDelete = (id: string) => {
    const confirmed = window.confirm("Delete this measurement?");
    if (!confirmed) return;
    const existing = measurementsRef.current.find(
      (measurement) => measurement.id === id
    );
    if (existing) {
      sendMessage({
        type: "mutation",
        entity: "measurement",
        action: "delete",
        id,
        expectedVersion: existing.version
      });
    }
    setMeasurements((prev) => prev.filter((measurement) => measurement.id !== id));
    if (selectedMeasurementId === id) {
      setSelectedMeasurementId(null);
    }
    if (mapPopout?.kind === "measurement" && mapPopout.id === id) {
      setMapPopout(null);
    }
    if (formMode === "edit" && selectedMeasurementId === id) {
      clearDraft();
    }
    trackAppEvent(TELEMETRY_EVENTS.MEASUREMENT_DELETED, {
      result: "success"
    });
    trackAppEvent(TELEMETRY_EVENTS.SESSION_UPDATED_LOCAL, {
      update_source: "measurement_delete"
    });
  };

  const handleResetSession = () => {
    const confirmed = window.confirm(
      "Reset this session? This clears shared entries for everyone."
    );
    if (!confirmed) return;
    sendMessage({ type: "reset_session" });
    setMeasurements([]);
    setSavedTargets([]);
    setSessionMeta((prev) =>
      prev ? { ...prev, lastEditedAt: new Date().toISOString() } : prev
    );
    clearDraft();
    clearTargetDraft();
    setMapPopout(null);
    setSelectedMeasurementId(null);
    if (sessionId) {
      clearSession(sessionId);
    }
    setStatusMessage("Session reset requested.");
    trackAppEvent(TELEMETRY_EVENTS.SESSION_RESET_REQUESTED, {
      measurement_count: measurements.length,
      saved_target_count: savedTargets.length
    });
  };

  const handleExport = () => {
    const session = buildSession(measurements, sessionMeta, savedTargets);
    const blob = new Blob([JSON.stringify(session, null, 2)], {
      type: "application/json"
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `azimetria-session-${new Date()
      .toISOString()
      .slice(0, 19)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    trackAppEvent(TELEMETRY_EVENTS.SESSION_EXPORTED, {
      measurement_count: measurements.length,
      saved_target_count: savedTargets.length
    });
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const confirmed = window.confirm(
      "Load this session? Current measurements will be replaced."
    );
    if (!confirmed) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text) as SessionData;
      if (!data.measurements) throw new Error("Invalid format");
      const normalizedMeasurements = data.measurements.map((measurement) => ({
        ...measurement,
        version: measurement.version ?? 1
      }));
      const normalizedTargets = Array.isArray(data.savedTargets)
        ? data.savedTargets.map((target) => ({
            ...target,
            version: target.version ?? 1,
            visible: target.visible ?? true
          }))
        : [];
      sendMessage({
        type: "replace_session",
        createdAt: data.createdAt ?? new Date().toISOString(),
        measurements: normalizedMeasurements,
        savedTargets: normalizedTargets
      });
      setMeasurements(normalizedMeasurements);
      setSavedTargets(normalizedTargets);
      setSessionMeta({
        createdAt: data.createdAt ?? new Date().toISOString(),
        lastEditedAt: new Date().toISOString()
      });
      clearDraft();
      clearTargetDraft();
      setMapPopout(null);
      setSelectedMeasurementId(null);
      setStatusMessage("Session loaded.");
      trackAppEvent(TELEMETRY_EVENTS.SESSION_IMPORTED, {
        result: "success",
        measurement_count: normalizedMeasurements.length,
        saved_target_count: normalizedTargets.length
      });
    } catch {
      setStatusMessage("Unable to import file.");
      trackAppEvent(TELEMETRY_EVENTS.SESSION_IMPORT_FAILED, {
        result: "failure",
        error_kind: "invalid_json"
      });
    }
  };

  const handleOpenCallsignEdit = () => {
    setCallsignDraft(callsign);
    setCallsignError(null);
    setCallsignSubmitting(false);
    setPendingCallsignSubmission(null);
    setCallsignDialogMode("edit");
  };

  const handleCallsignSubmit = () => {
    if (!sessionId || collabStatus !== "connected") {
      setCallsignError("Connect to the session before setting a callsign.");
      return;
    }
    const next = normalizeCallsign(callsignDraft);
    const validationError = getCallsignValidationError(next, presence);
    if (validationError) {
      setCallsignError(validationError);
      return;
    }
    if (
      callsignDialogMode === "edit" &&
      canonicalizeCallsign(next) === canonicalizeCallsign(callsign)
    ) {
      setCallsignDialogMode(null);
      setCallsignSubmitting(false);
      setPendingCallsignSubmission(null);
      setCallsignError(null);
      return;
    }
    setCallsignDraft(next);
    setCallsignError(null);
    setCallsignSubmitting(true);
    setPendingCallsignSubmission(next);
    if (callsignDialogMode === "join") {
      trackAppEvent(TELEMETRY_EVENTS.SESSION_JOIN_REQUESTED, {
        source: "manual_callsign_join"
      });
      sendMessage({
        type: "join",
        sessionId,
        userId,
        callsign: next
      });
      return;
    }
    sendMessage({ type: "update_callsign", callsign: next });
  };

  const handleCallsignCancel = () => {
    setCallsignDraft(callsign);
    setCallsignError(null);
    setCallsignSubmitting(false);
    setPendingCallsignSubmission(null);
    setCallsignDialogMode(null);
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard not available");
      }
      await navigator.clipboard.writeText(shareLink);
      setStatusMessage("Share link copied.");
      trackAppEvent(TELEMETRY_EVENTS.SHARE_LINK_COPY_SUCCEEDED, {
        result: "success"
      });
    } catch {
      setStatusMessage("Unable to copy share link.");
      trackAppEvent(TELEMETRY_EVENTS.SHARE_LINK_COPY_FAILED, {
        result: "failure",
        error_kind: "clipboard_unavailable"
      });
    }
  };

  const buildTargetName = (suffix?: string) => {
    const base = targetName.trim() || `Target ${savedTargets.length + 1}`;
    return suffix ? `${base} ${suffix}` : base;
  };

  const handleSaveTarget = () => {
    if (!overallEstimate) return;
    const target: SavedTarget = {
      id: crypto.randomUUID(),
      version: 1,
      name: buildTargetName(),
      lat: overallEstimate.lat,
      lon: overallEstimate.lon,
      createdAt: new Date().toISOString(),
      visible: true
    };
    sendMessage({
      type: "mutation",
      entity: "savedTarget",
      action: "create",
      item: target
    });
    setSavedTargets((prev) => upsertById(prev, target));
    setStatusMessage("Target saved.");
    trackAppEvent(TELEMETRY_EVENTS.TARGET_SAVED, {
      estimate_source: "overall_estimate",
      result: "success"
    });
    trackAppEvent(TELEMETRY_EVENTS.SESSION_UPDATED_LOCAL, {
      update_source: "target_save_overall"
    });
    applyTargetGeocode(target.id, target.lat, target.lon, target.version);
  };

  const handleSaveTargetAt = (lat: number, lon: number) => {
    const target: SavedTarget = {
      id: crypto.randomUUID(),
      version: 1,
      name: buildTargetName(),
      lat,
      lon,
      createdAt: new Date().toISOString(),
      visible: true
    };
    sendMessage({
      type: "mutation",
      entity: "savedTarget",
      action: "create",
      item: target
    });
    setSavedTargets((prev) => upsertById(prev, target));
    setStatusMessage("Point saved.");
    trackAppEvent(TELEMETRY_EVENTS.TARGET_SAVED, {
      estimate_source: "manual_point",
      result: "success"
    });
    trackAppEvent(TELEMETRY_EVENTS.SESSION_UPDATED_LOCAL, {
      update_source: "target_save_point"
    });
    applyTargetGeocode(target.id, target.lat, target.lon, target.version);
  };

  const handleSaveCandidate = (lat: number, lon: number, suffix: string) => {
    const target: SavedTarget = {
      id: crypto.randomUUID(),
      version: 1,
      name: buildTargetName(suffix),
      lat,
      lon,
      createdAt: new Date().toISOString(),
      visible: true
    };
    sendMessage({
      type: "mutation",
      entity: "savedTarget",
      action: "create",
      item: target
    });
    setSavedTargets((prev) => upsertById(prev, target));
    setStatusMessage(`Target ${suffix} saved.`);
    trackAppEvent(TELEMETRY_EVENTS.TARGET_SAVED, {
      estimate_source: "candidate",
      result: "success"
    });
    trackAppEvent(TELEMETRY_EVENTS.SESSION_UPDATED_LOCAL, {
      update_source: "target_save_candidate"
    });
    applyTargetGeocode(target.id, target.lat, target.lon, target.version);
  };

  const handleToggleTarget = (id: string) => {
    const existing = savedTargetsRef.current.find((target) => target.id === id);
    if (!existing) return;
    const nextVisible = !existing.visible;
    sendMessage({
      type: "mutation",
      entity: "savedTarget",
      action: "update",
      id,
      expectedVersion: existing.version,
      updates: { visible: nextVisible }
    });
    setSavedTargets((prev) =>
      prev.map((target) =>
        target.id === id
          ? { ...target, visible: nextVisible, version: target.version + 1 }
          : target
      )
    );
    trackAppEvent(TELEMETRY_EVENTS.TARGET_VISIBILITY_TOGGLED, {
      visible: nextVisible
    });
    trackAppEvent(TELEMETRY_EVENTS.SESSION_UPDATED_LOCAL, {
      update_source: "target_visibility_toggle"
    });
  };

  const handleDeleteTarget = (id: string) => {
    const confirmed = window.confirm("Delete this saved target?");
    if (!confirmed) return;
    const existing = savedTargetsRef.current.find((target) => target.id === id);
    if (existing) {
      sendMessage({
        type: "mutation",
        entity: "savedTarget",
        action: "delete",
        id,
        expectedVersion: existing.version
      });
    }
    setSavedTargets((prev) => prev.filter((target) => target.id !== id));
    trackAppEvent(TELEMETRY_EVENTS.TARGET_DELETED, {
      result: "success"
    });
    trackAppEvent(TELEMETRY_EVENTS.SESSION_UPDATED_LOCAL, {
      update_source: "target_delete"
    });
  };

  const startTargetEdit = (target: SavedTarget) => {
    setTargetDraft({
      id: target.id,
      version: target.version,
      name: target.name,
      lat: target.lat,
      lon: target.lon,
      visible: target.visible
    });
    setTargetEditNotice(null);
  };

  const updateTargetDraft = (updates: Partial<SavedTargetDraft>) => {
    if (!targetDraft) return;
    setTargetDraft({ ...targetDraft, ...updates });
  };

  const handleTargetEditSave = () => {
    if (!targetDraft) return;
    if (!targetDraft.name.trim()) {
      setTargetEditNotice("Name is required.");
      return;
    }
    if (
      Number.isNaN(targetDraft.lat) ||
      targetDraft.lat < -90 ||
      targetDraft.lat > 90
    ) {
      setTargetEditNotice("Latitude must be between -90 and 90.");
      return;
    }
    if (
      Number.isNaN(targetDraft.lon) ||
      targetDraft.lon < -180 ||
      targetDraft.lon > 180
    ) {
      setTargetEditNotice("Longitude must be between -180 and 180.");
      return;
    }
    const updates = {
      name: targetDraft.name,
      lat: targetDraft.lat,
      lon: targetDraft.lon,
      visible: targetDraft.visible
    };
    sendMessage({
      type: "mutation",
      entity: "savedTarget",
      action: "update",
      id: targetDraft.id,
      expectedVersion: targetDraft.version,
      updates
    });
    setSavedTargets((prev) =>
      prev.map((target) =>
        target.id === targetDraft.id
          ? { ...target, ...updates, version: target.version + 1 }
          : target
      )
    );
    clearTargetDraft();
    setStatusMessage("Target updated.");
    trackAppEvent(TELEMETRY_EVENTS.TARGET_UPDATED, {
      result: "success"
    });
    trackAppEvent(TELEMETRY_EVENTS.SESSION_UPDATED_LOCAL, {
      update_source: "target_update"
    });
    applyTargetGeocode(
      targetDraft.id,
      targetDraft.lat,
      targetDraft.lon,
      targetDraft.version + 1
    );
  };

  const popoutMeasurement = useMemo(() => {
    if (mapPopout?.kind !== "measurement") return null;
    return measurements.find((measurement) => measurement.id === mapPopout.id) ?? null;
  }, [mapPopout, measurements]);

  const popoutPosition = useMemo(() => {
    if (!mapPopout) return null;
    if (mapPopout.kind === "draft") {
      return { lat: mapPopout.lat, lon: mapPopout.lon };
    }
    if (popoutMeasurement) {
      return { lat: popoutMeasurement.lat, lon: popoutMeasurement.lon };
    }
    return null;
  }, [mapPopout, popoutMeasurement]);

  const popoutLatLng = useMemo<LatLngExpression | null>(() => {
    if (!popoutPosition) return null;
    return [popoutPosition.lat, popoutPosition.lon] as [number, number];
  }, [popoutPosition?.lat, popoutPosition?.lon]);

  useEffect(() => {
    if (mapPopout?.kind === "measurement" && !popoutMeasurement) {
      setMapPopout(null);
    }
  }, [mapPopout, popoutMeasurement]);

  const renderMeasurementForm = (
    mode: "add" | "edit",
    onCancel: () => void
  ) => {
    if (!draft) return null;
    return (
      <form onSubmit={handleSubmit} className="form-grid">
        <div className="input-group">
          <label>Type</label>
          <select
            value={draft.type}
            onChange={(event) =>
              updateDraft({ type: event.target.value as MeasurementType })
            }
          >
            <option value="bearing">Bearing / azimuth</option>
            <option value="distance">Distance / range</option>
          </select>
        </div>
        <div className="input-group">
          <label>Latitude</label>
          <input value={formatCoordinate(draft.lat)} disabled />
        </div>
        <div className="input-group">
          <label>Longitude</label>
          <input value={formatCoordinate(draft.lon)} disabled />
        </div>
        <div className="input-group">
          <label>{draft.type === "bearing" ? "Bearing" : "Distance"}</label>
          <input
            type="text"
            inputMode="decimal"
            value={displayNumericFieldValue(draft.value)}
            onChange={(event) => {
              const raw = event.target.value;
              if (!isDecimalInputToken(raw)) return;
              if (isIncompleteDecimalToken(raw)) return;
              const parsed = Number(raw);
              if (Number.isNaN(parsed)) return;
              updateDraft({ value: parsed });
            }}
            required
          />
        </div>
        {draft.type === "distance" ? (
          <>
            <div className="input-group">
              <label>Units</label>
              <select
                value={draft.units}
                onChange={(event) =>
                  updateDraft({ units: event.target.value as DistanceUnit })
                }
              >
                {distanceUnits.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
            <div className="input-group">
              <label>Margin of error (±%)</label>
              <input
                type="text"
                inputMode="decimal"
                value={displayNumericFieldValue(draft.distanceErrorPct)}
                onChange={(event) => {
                  const raw = event.target.value;
                  if (raw === "") {
                    updateDraft({ distanceErrorPct: undefined });
                    return;
                  }
                  if (!isDecimalInputToken(raw)) return;
                  if (isIncompleteDecimalToken(raw)) return;
                  const parsed = Number(raw);
                  if (Number.isNaN(parsed)) return;
                  updateDraft({ distanceErrorPct: parsed });
                }}
                placeholder="Optional"
              />
              <span className="hint">Adds inner/outer range rings.</span>
            </div>
          </>
        ) : (
          <>
            <div className="input-group">
              <label>North reference</label>
              <select
                value={draft.northReference}
                onChange={(event) =>
                  updateDraft({
                    northReference: event.target.value as NorthReference
                  })
                }
              >
                <option value="true">True north</option>
                <option value="magnetic">Magnetic north</option>
              </select>
            </div>
            <div className="input-group">
              <label>Bearing uncertainty (±°)</label>
              <input
                type="text"
                inputMode="decimal"
                value={displayNumericFieldValue(draft.bearingErrorDeg)}
                onChange={(event) => {
                  const raw = event.target.value;
                  if (raw === "") {
                    updateDraft({ bearingErrorDeg: undefined });
                    return;
                  }
                  if (!isDecimalInputToken(raw)) return;
                  if (isIncompleteDecimalToken(raw)) return;
                  const parsed = Number(raw);
                  if (Number.isNaN(parsed)) return;
                  updateDraft({ bearingErrorDeg: parsed });
                }}
                placeholder="Optional"
              />
              <span className="hint">Creates a cone around the bearing.</span>
            </div>
          </>
        )}
        {draft.type === "bearing" && draft.northReference === "magnetic" && (
          <div className="input-group">
            <label>Declination</label>
            <input
              type="text"
              inputMode="decimal"
              value={displayNumericFieldValue(draft.declination)}
              onChange={(event) => {
                const raw = event.target.value;
                if (!isDecimalInputToken(raw, true)) return;
                if (isIncompleteDecimalToken(raw)) return;
                const parsed = Number(raw);
                if (Number.isNaN(parsed)) return;
                updateDraft({ declination: parsed });
              }}
            />
            <span className="hint">Default: {DEFAULT_DECLINATION}°</span>
          </div>
        )}
        {formError && <p className="error">{formError}</p>}
        {formNotice && <p className="notice">{formNotice}</p>}
        <div className="button-row">
          <button className="primary" type="submit">
            {mode === "edit" ? "Update" : "Add measurement"}
          </button>
          <button className="ghost" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    );
  };

  const callsignValidationError =
    callsignError ??
    (callsignDialogMode
      ? getCallsignValidationError(callsignDraft, presence)
      : null);
  const callsignDialogOpen = callsignDialogMode !== null;
  const joinDialogOpen = callsignDialogMode === "join";
  const canSubmitCallsign =
    callsignDialogOpen &&
    Boolean(sessionId) &&
    collabStatus === "connected" &&
    callsignValidationError === null;

  return (
    <div className={`app ${sidebarOpen ? "sidebar-open" : "sidebar-collapsed"}`}>
      <div className={`app-shell ${joinDialogOpen ? "is-muted" : ""}`}>
      <header className="top-bar">
        <div>
          <p className="eyebrow">Azimetria</p>
        </div>
        <div className="top-actions">
          <div className="presence-bar">
            <div className={`presence-status ${collabStatus}`} />
            <span className="presence-count">{presence.length} online</span>
            <div className="presence-avatars">
              {presence.map((user) => (
                <span
                  key={user.userId}
                  className="presence-avatar"
                  style={{ backgroundColor: user.color }}
                  title={user.callsign}
                >
                  {abbreviateCallsign(user.callsign)}
                </span>
              ))}
            </div>
            <button
              className="ghost"
              type="button"
              onClick={handleOpenCallsignEdit}
              disabled={!joinedSession}
            >
              Edit callsign
            </button>
          </div>
          <button
            className="ghost"
            onClick={() =>
              setSidebarOpen((prev) => {
                const next = !prev;
                trackAppEvent(TELEMETRY_EVENTS.SIDEBAR_TOGGLED, {
                  sidebar_open: next
                });
                return next;
              })
            }
          >
            {sidebarOpen ? "Hide" : "Show"} panel
          </button>
          <ViewSettingsMenu
            mode={advancedMode ? "adaptive" : "basic"}
            showRemoteCursors={showRemoteCursors}
            simulateRandomCursors={simulateRandomCursors}
            onSelectMode={(mode) => {
              setAdvancedMode(mode === "adaptive");
              trackAppEvent(TELEMETRY_EVENTS.VIEW_MODE_CHANGED, {
                view_mode: mode
              });
            }}
            onToggleRemoteCursors={() =>
              setShowRemoteCursors((prev) => {
                const next = !prev;
                trackAppEvent(TELEMETRY_EVENTS.REMOTE_CURSORS_TOGGLED, {
                  remote_cursors_visible: next
                });
                return next;
              })
            }
            onToggleSimulateRandomCursors={() =>
              setSimulateRandomCursors((prev) => !prev)
            }
          />
        </div>
      </header>

      <main className="layout">
        <aside className="sidebar">
          <section className="panel">
            <div className="panel-title">
              <h2>Session controls</h2>
              <span>
                {sessionMeta
                  ? new Date(sessionMeta.createdAt).toLocaleDateString()
                  : "—"}
              </span>
            </div>
            <div className="share-block">
              <label>Share link</label>
              <div className="share-row">
                <input value={shareLink} readOnly disabled={!shareLink} />
                <button
                  className="ghost"
                  onClick={handleCopyLink}
                  type="button"
                  disabled={!shareLink}
                >
                  Copy
                </button>
              </div>
              {sessionId && (
                <p className="muted">Session ID: {sessionId}</p>
              )}
              <p className="muted">
                {collabStatus === "connected"
                  ? "Live sync connected"
                  : collabStatus === "connecting"
                    ? "Connecting to session…"
                    : collabStatus === "disconnected"
                      ? "Reconnecting…"
                      : "Waiting for session"}
              </p>
            </div>
            <div className="button-row">
              <button className="primary" onClick={handleExport}>
                Export JSON
              </button>
              <button className="ghost" onClick={handleImportClick}>
                Import JSON
              </button>
              <button className="ghost" onClick={handleResetSession}>
                Reset session
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              onChange={handleImport}
              className="hidden"
            />
            <div className="toggle-row">
              <label>
                <input
                  type="checkbox"
                  checked={geocodeEnabled}
                  onChange={() =>
                    setGeocodeEnabled((prev) => {
                      const next = !prev;
                      trackAppEvent(TELEMETRY_EVENTS.GEOCODE_TOGGLED, {
                        geocode_enabled: next
                      });
                      return next;
                    })
                  }
                />
                Reverse-geocode place names
              </label>
              <span className="muted">Uses Nominatim when online</span>
            </div>
            <button className="primary" onClick={handleUseLocation}>
              Use my location
            </button>
            {statusMessage && <p className="status">{statusMessage}</p>}
            {advancedMode && (
              <div className="callout">
                Adaptive features are planned for collaboration and analytics.
              </div>
            )}
          </section>

          <section className="panel">
            <div className="panel-title">
              <h2>Measurements</h2>
              <span className="muted">{measurements.length} total</span>
            </div>
            {measurements.length === 0 ? (
              <p className="empty">No measurements yet.</p>
            ) : (
              <div className="list">
                {measurements.map((measurement, index) => {
                  const editors = getEditors("measurement", measurement.id);
                  return (
                    <div
                      key={measurement.id}
                      className={`list-item ${
                        selectedMeasurementId === measurement.id
                          ? "is-selected"
                          : ""
                      }`}
                      onClick={() => {
                        openMeasurementView(measurement);
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="list-content">
                        <h3>
                          {index + 1}. {measurement.type}
                        </h3>
                        <p className="muted">
                          {measurement.type === "bearing"
                            ? formatBearingWithError(measurement)
                            : formatDistanceWithError(measurement)}
                        </p>
                        {measurement.type === "distance" &&
                          measurement.distanceErrorPct &&
                          measurement.distanceErrorPct > 0 && (
                            <p className="muted">
                              Range: {formatDistanceRange(measurement)}
                            </p>
                          )}
                        {measurement.type === "bearing" &&
                          measurement.northReference === "magnetic" && (
                            <p className="muted">
                              True bearing: {formatTrueBearingWithError(measurement)}
                            </p>
                          )}
                        <p className="muted">
                          {formatCoordinate(measurement.lat)}, {formatCoordinate(
                            measurement.lon
                          )}
                        </p>
                        {measurement.placeName && (
                          <p className="muted">{measurement.placeName}</p>
                        )}
                        {measurement.plusCode && (
                          <p className="muted">Plus Code: {measurement.plusCode}</p>
                        )}
                        {editors.length > 0 && (
                          <div className="editing-row">
                            {editors.map((editor) => (
                              <span
                                key={editor.userId}
                                className="editing-badge"
                                title={`${editor.callsign} editing`}
                              >
                                <span
                                  className="editing-dot"
                                  style={{ backgroundColor: editor.color }}
                                />
                                {editor.callsign}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="item-actions">
                        <button
                          className="ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEdit(measurement);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="ghost danger"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(measurement.id);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="panel">
            <div className="panel-title">
              <h2>Estimates</h2>
              <span className="muted">
                {isTwoDistanceOnly
                  ? "Two possible intersections"
                  : hasTwoMeasurementAmbiguity
                    ? "Multiple possible intersections"
                    : hasTwoMeasurementConflict
                      ? "No overlap between inputs"
                  : overallEstimate
                    ? confidence.all
                      ? "Overall estimate from all inputs"
                      : consensusEstimates.length > 0
                        ? "Overall estimate from consensus average"
                        : "Overall estimate from broad overlap"
                    : hasSingleBearingOnly
                      ? "Awaiting another measurement"
                    : hasSingleDistanceOnlyWithUncertainty
                      ? "Awaiting another measurement"
                    : "Awaiting inputs"}
              </span>
            </div>
            {isTwoDistanceOnly || hasTwoMeasurementAmbiguity ? (
              <div className="estimate">
                {candidateEstimates.length === 0 ? (
                  <p className="empty">
                    {isTwoDistanceOnly
                      ? "Circles do not intersect. Adjust distances or add another measurement."
                      : "Measurements do not overlap. Adjust values or uncertainty and try again."}
                  </p>
                ) : (
                  <>
                    <div className="input-group">
                      <label>Base name</label>
                      <input
                        value={targetName}
                        onChange={(event) => setTargetName(event.target.value)}
                      />
                    </div>
                    <div className="candidate-grid">
                      {candidateEstimates.map((candidate, index) => {
                        const suffix =
                          index < 26
                            ? String.fromCharCode(65 + index)
                            : `${index + 1}`;
                        return (
                          <div key={`${candidate.lat}-${candidate.lon}`} className="candidate">
                            <p className="coords">
                              {formatCoordinate(candidate.lat)},{" "}
                              {formatCoordinate(candidate.lon)}
                            </p>
                            <div className="button-row">
                              <button
                                className="ghost"
                                onClick={() => {
                                  setCandidateFocus(candidate);
                                }}
                              >
                                Focus
                              </button>
                              <button
                                className="primary"
                                onClick={() =>
                                  handleSaveCandidate(
                                    candidate.lat,
                                    candidate.lon,
                                    suffix
                                  )
                                }
                              >
                                Save {suffix}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ) : hasTwoMeasurementConflict ? (
              <p className="empty">
                Measurements do not overlap. Adjust values or uncertainty and
                try again.
              </p>
            ) : overallEstimate ? (
              <div className="estimate">
                <div className="estimate-block">
                  <p className="label">
                    {confidence.all
                      ? "Overall estimate (all inputs)"
                      : consensusEstimates.length > 0
                        ? "Overall estimate (consensus average)"
                        : "Overall estimate (broad overlap)"}
                  </p>
                  <p className="coords">
                    {formatCoordinate(overallEstimate.lat)},{" "}
                    {formatCoordinate(overallEstimate.lon)}
                  </p>
                  {overallEstimatePlaceName && (
                    <p className="muted">{overallEstimatePlaceName}</p>
                  )}
                  <div className="input-group">
                    <label>Target name</label>
                    <input
                      value={targetName}
                      onChange={(event) => setTargetName(event.target.value)}
                    />
                  </div>
                  <button className="primary" onClick={handleSaveTarget}>
                    Save target
                  </button>
                </div>
                {consensusEstimates.length > 0 ? (
                  <div className="estimate-block">
                    <p className="label">
                      Outlier-ignored estimates (drop {consensusEstimates[0].excludedCount})
                    </p>
                    <div className="candidate-grid">
                      {consensusEstimates.map((candidate, index) => (
                        <div key={candidate.id} className="candidate">
                          <p className="coords">
                            {formatCoordinate(candidate.lat)},{" "}
                            {formatCoordinate(candidate.lon)}
                          </p>
                          {consensusPlaceNames[candidate.id] && (
                            <p className="muted">
                              {consensusPlaceNames[candidate.id]}
                            </p>
                          )}
                          <p className="muted">
                            Excluded {candidate.excludedCount} measurement
                            {candidate.excludedCount === 1 ? "" : "s"}
                          </p>
                          <div className="button-row">
                            <button
                              className="ghost"
                              onClick={() => setCandidateFocus(candidate)}
                            >
                              Focus
                            </button>
                            <button
                              className="ghost"
                              onClick={() =>
                                handleSaveCandidate(
                                  candidate.lat,
                                  candidate.lon,
                                  `Alt ${index + 1}`
                                )
                              }
                            >
                              Save Alt {index + 1}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="estimate-block">
                    <p className="label">Outlier-ignored estimates</p>
                    <p className="muted">
                      No alternative consensus regions found yet.
                    </p>
                  </div>
                )}
              </div>
            ) : hasSingleBearingOnly ? (
              <p className="empty">
                A single bearing shows direction only. Add another measurement to
                estimate a target.
              </p>
            ) : hasSingleDistanceOnlyWithUncertainty ? (
              <p className="empty">
                A single distance range with margin of error cannot determine a
                target. Add another measurement.
              </p>
            ) : (
              <p className="empty">
                Add at least one measurement to see the provisional estimate.
              </p>
            )}
          </section>

          <section className="panel">
            <div className="panel-title">
              <h2>Saved targets</h2>
              <span className="muted">{savedTargets.length} saved</span>
            </div>
            {savedTargets.length === 0 ? (
              <p className="empty">No saved targets yet.</p>
            ) : (
              <div className="list">
                {savedTargets.map((target) => {
                  const editors = getEditors("savedTarget", target.id);
                  const isEditing = targetDraft?.id === target.id;
                  return (
                    <div key={target.id} className="list-item">
                      <div className="list-content">
                        {isEditing && targetDraft ? (
                          <form
                            className="form-grid compact"
                            onSubmit={(event) => {
                              event.preventDefault();
                              handleTargetEditSave();
                            }}
                          >
                            <div className="input-group">
                              <label>Name</label>
                              <input
                                value={targetDraft.name}
                                onChange={(event) =>
                                  updateTargetDraft({ name: event.target.value })
                                }
                              />
                            </div>
                            <div className="input-group">
                              <label>Latitude</label>
                              <input
                                type="number"
                                step={0.0001}
                                value={targetDraft.lat}
                                onChange={(event) =>
                                  updateTargetDraft({
                                    lat: Number(event.target.value)
                                  })
                                }
                              />
                            </div>
                            <div className="input-group">
                              <label>Longitude</label>
                              <input
                                type="number"
                                step={0.0001}
                                value={targetDraft.lon}
                                onChange={(event) =>
                                  updateTargetDraft({
                                    lon: Number(event.target.value)
                                  })
                                }
                              />
                            </div>
                            <label className="checkbox-row">
                              <input
                                type="checkbox"
                                checked={targetDraft.visible}
                                onChange={() =>
                                  updateTargetDraft({
                                    visible: !targetDraft.visible
                                  })
                                }
                              />
                              Visible on map
                            </label>
                            {targetEditNotice && (
                              <p className="notice">{targetEditNotice}</p>
                            )}
                            <div className="button-row">
                              <button className="primary" type="submit">
                                Save
                              </button>
                              <button
                                className="ghost"
                                type="button"
                                onClick={clearTargetDraft}
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <h3>{target.name}</h3>
                            <p className="muted">
                              {formatCoordinate(target.lat)},{" "}
                              {formatCoordinate(target.lon)}
                            </p>
                            {target.placeName && (
                              <p className="muted">{target.placeName}</p>
                            )}
                            <p className="muted">{formatDateTime(target.createdAt)}</p>
                            {editors.length > 0 && (
                              <div className="editing-row">
                                {editors.map((editor) => (
                                  <span
                                    key={editor.userId}
                                    className="editing-badge"
                                    title={`${editor.callsign} editing`}
                                  >
                                    <span
                                      className="editing-dot"
                                      style={{ backgroundColor: editor.color }}
                                    />
                                    {editor.callsign}
                                  </span>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {!isEditing && (
                        <div className="item-actions">
                          <button
                            className="ghost"
                            onClick={() => startTargetEdit(target)}
                          >
                            Edit
                          </button>
                          <button
                            className="ghost"
                            onClick={() => handleToggleTarget(target.id)}
                          >
                            {target.visible ? "Hide" : "Show"}
                          </button>
                          <button
                            className="ghost danger"
                            onClick={() => handleDeleteTarget(target.id)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </aside>

        <section className="map-shell">
          <MapContainer
            center={[mapView.lat, mapView.lon]}
            zoom={mapView.zoom}
            minZoom={2}
            zoomControl={false}
            className="map"
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapInstanceBridge onMapReady={setMapInstance} />
            <MapEvents
              onClick={handleMapClick}
              onMove={handleMapMove}
              onCursorMove={handleMapCursorMove}
              onCursorLeave={handleMapCursorLeave}
            />
            <MapZoomControls
              onZoomToFitPoints={handleZoomToFitPoints}
              onZoomToFitUsers={handleZoomToFitUsers}
            />
            <MapSizeSync />
            <MapFocus focus={focusPoint} />
            {showRemoteCursors && visibleRemoteCursors.length > 0 && (
              <RemoteCursorOverlay cursors={visibleRemoteCursors} />
            )}

            {mapPopout?.kind === "draft" && draft && (
              <Marker position={[draft.lat, draft.lon]} icon={markerIcons.pending} />
            )}

            {mapPopout && popoutLatLng && (
              <Popup
                position={popoutLatLng}
                autoClose={false}
                closeOnClick={false}
                eventHandlers={{
                  remove: handlePopoutClose,
                  click: (event) => {
                    L.DomEvent.stopPropagation(event);
                  },
                  mousedown: (event) => {
                    L.DomEvent.stopPropagation(event);
                  },
                  dblclick: (event) => {
                    L.DomEvent.stopPropagation(event);
                  }
                }}
              >
                <div
                  className="popup map-popout"
                  ref={popoutContentRef}
                  onClick={stopPopoutPropagation}
                  onPointerDown={handlePopoutPointerDown}
                  onDoubleClick={stopPopoutPropagation}
                >
                  {mapPopout.kind === "draft" ? (
                    mapPopout.mode === "menu" ? (
                      <>
                        <strong>New point</strong>
                        {draft && (
                          <p>
                            {formatCoordinate(draft.lat)}, {" "}
                            {formatCoordinate(draft.lon)}
                          </p>
                        )}
                        <div className="button-row">
                          <button
                            className="primary"
                            type="button"
                            onClick={handleDraftAddMeasurement}
                          >
                            Add measurement
                          </button>
                          <button
                            className="ghost"
                            type="button"
                            onClick={handleDraftAddPoint}
                          >
                            Add point
                          </button>
                          <button
                            className="ghost"
                            type="button"
                            onClick={handleCancelDraft}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <strong>Add measurement</strong>
                        {renderMeasurementForm("add", handleCancelDraft)}
                      </>
                    )
                  ) : popoutMeasurement ? (
                    mapPopout.mode === "edit" ? (
                      <>
                        <strong>Edit measurement</strong>
                        {renderMeasurementForm("edit", handleCancelEdit)}
                      </>
                    ) : (
                      <>
                        <strong>{popoutMeasurement.type}</strong>
                        <p>
                          {popoutMeasurement.type === "bearing"
                            ? formatBearingWithError(popoutMeasurement)
                            : formatDistanceWithError(popoutMeasurement)}
                        </p>
                        {popoutMeasurement.type === "distance" &&
                          popoutMeasurement.distanceErrorPct &&
                          popoutMeasurement.distanceErrorPct > 0 && (
                            <p>Range: {formatDistanceRange(popoutMeasurement)}</p>
                          )}
                        {popoutMeasurement.type === "bearing" &&
                          popoutMeasurement.northReference === "magnetic" && (
                            <p>
                              True: {formatTrueBearingWithError(popoutMeasurement)}
                            </p>
                          )}
                        <p>
                          {formatCoordinate(popoutMeasurement.lat)}, {" "}
                          {formatCoordinate(popoutMeasurement.lon)}
                        </p>
                        {popoutMeasurement.placeName && (
                          <p>{popoutMeasurement.placeName}</p>
                        )}
                        {popoutMeasurement.plusCode && (
                          <p>{popoutMeasurement.plusCode}</p>
                        )}
                        {getEditors("measurement", popoutMeasurement.id).length > 0 && (
                          <div className="editing-row">
                            {getEditors("measurement", popoutMeasurement.id).map(
                              (editor) => (
                                <span
                                  key={editor.userId}
                                  className="editing-badge"
                                  title={`${editor.callsign} editing`}
                                >
                                  <span
                                    className="editing-dot"
                                    style={{ backgroundColor: editor.color }}
                                  />
                                  {editor.callsign}
                                </span>
                              )
                            )}
                          </div>
                        )}
                        <div className="button-row">
                          <button
                            className="primary"
                            type="button"
                            onClick={() => openMeasurementEdit(popoutMeasurement)}
                          >
                            Edit
                          </button>
                          <button
                            className="ghost"
                            type="button"
                            onClick={handlePopoutClose}
                          >
                            Close
                          </button>
                          <button
                            className="ghost danger"
                            type="button"
                            onClick={() => handleDelete(popoutMeasurement.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )
                  ) : null}
                </div>
              </Popup>
            )}

            {confidenceDisplay.union && !confidenceDisplay.all && (
              <GeoJSON
                key={`confidence-union-${geometryKeySeed}`}
                data={confidenceDisplay.union}
                style={{
                  color: "#c36f43",
                  fillColor: "#c36f43",
                  fillOpacity: 0.25,
                  weight: 1
                }}
              />
            )}
            {confidenceDisplay.allButOne && (
              <GeoJSON
                key={`confidence-allbutone-${geometryKeySeed}`}
                data={confidenceDisplay.allButOne}
                style={{
                  color: "#e0c55a",
                  fillColor: "#e0c55a",
                  fillOpacity: 0.3,
                  weight: 1
                }}
              />
            )}
            {confidenceDisplay.all && (
              <GeoJSON
                key={`confidence-all-${geometryKeySeed}`}
                data={confidenceDisplay.all}
                style={{
                  color: "#3b9b7d",
                  fillColor: "#3b9b7d",
                  fillOpacity: 0.35,
                  weight: 1
                }}
              />
            )}

            {measurements.map((measurement, index) => {
              const geometry = measurementGeometries[index];
              const measurementKey = measurementGeometryKey(measurement);
              const distanceErrorPct =
                measurement.type === "distance"
                  ? measurement.distanceErrorPct ?? 0
                  : 0;
              const circleRadii = geometry?.circleRadiiMeters ?? [];
              return (
                <div key={measurement.id}>
                  {measurement.type === "distance" &&
                    distanceErrorPct > 0 &&
                    geometry?.polygon && (
                      <GeoJSON
                        key={`distance-band-${measurementKey}`}
                        data={geometry.polygon}
                        style={{
                          color: "#4b78c1",
                          fillColor: "#4b78c1",
                          fillOpacity: 0.12,
                          weight: 1
                        }}
                      />
                    )}
                  {circleRadii.map((radius, radiusIndex) => (
                    <Circle
                      key={`distance-circle-${measurementKey}-${radiusIndex}`}
                      center={[measurement.lat, measurement.lon]}
                      radius={radius}
                      pathOptions={{
                        color: "#4b78c1",
                        fillOpacity: distanceErrorPct > 0 ? 0 : 0.1,
                        weight: 1
                      }}
                    />
                  ))}
                  {geometry?.line && (
                    <Polyline
                      positions={geometry.line.map(([lon, lat]) => [lat, lon])}
                      pathOptions={{ color: "#a75b3c", weight: 2 }}
                    />
                  )}
                  {geometry?.polygon && measurement.type === "bearing" && (
                    <GeoJSON
                      key={`bearing-${measurementKey}`}
                      data={geometry.polygon}
                      style={{
                        color: "#a75b3c",
                        fillColor: "#a75b3c",
                        fillOpacity: 0.12,
                        weight: 1
                      }}
                    />
                  )}
                  <Marker
                    position={[measurement.lat, measurement.lon]}
                    icon={markerIcons.added}
                    eventHandlers={{
                      click: (event) => {
                        event.originalEvent?.stopPropagation();
                        openMeasurementView(measurement);
                      }
                    }}
                  />
                </div>
              );
            })}

            {overallEstimate && (
              <Marker
                position={[overallEstimate.lat, overallEstimate.lon]}
                icon={markerIcons.estimate}
              >
                <Popup>
                  <div className="popup">
                    <strong>Overall estimate</strong>
                    <p>Based on all inputs</p>
                    <p>
                      {formatCoordinate(overallEstimate.lat)}, {" "}
                      {formatCoordinate(overallEstimate.lon)}
                    </p>
                    {overallEstimatePlaceName && <p>{overallEstimatePlaceName}</p>}
                  </div>
                </Popup>
              </Marker>
            )}

            {candidateEstimates.map((candidate, index) => (
              <Marker
                key={`candidate-${index}`}
                position={[candidate.lat, candidate.lon]}
                icon={markerIcons.estimate}
              >
                <Popup>
                  <div className="popup">
                    <strong>
                      Candidate{" "}
                      {index < 26 ? String.fromCharCode(65 + index) : index + 1}
                    </strong>
                    <p>
                      {formatCoordinate(candidate.lat)},{" "}
                      {formatCoordinate(candidate.lon)}
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}

            {consensusEstimates.map((candidate, index) => (
              <Marker
                key={`consensus-${candidate.id}`}
                position={[candidate.lat, candidate.lon]}
                icon={markerIcons.outlier}
              >
                <Popup>
                  <div className="popup">
                    <strong>Outlier-ignored estimate {index + 1}</strong>
                    <p>
                      Excluded {candidate.excludedCount} measurement
                      {candidate.excludedCount === 1 ? "" : "s"}
                    </p>
                    <p>
                      {formatCoordinate(candidate.lat)}, {" "}
                      {formatCoordinate(candidate.lon)}
                    </p>
                    {consensusPlaceNames[candidate.id] && (
                      <p>{consensusPlaceNames[candidate.id]}</p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {savedTargets
              .filter((target) => target.visible)
              .map((target) => (
                <Marker
                  key={target.id}
                  position={[target.lat, target.lon]}
                  icon={markerIcons.saved}
                >
                  <Popup>
                    <div className="popup">
                      <strong>{target.name}</strong>
                      <p>
                        {formatCoordinate(target.lat)}, {" "}
                        {formatCoordinate(target.lon)}
                      </p>
                      {target.placeName && <p>{target.placeName}</p>}
                      <p>{formatDateTime(target.createdAt)}</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
          </MapContainer>
        </section>
      </main>
      </div>
      {callsignDialogMode && (
        <CallsignDialog
          mode={callsignDialogMode}
          draft={callsignDraft}
          error={callsignValidationError}
          canSubmit={canSubmitCallsign}
          isSubmitting={callsignSubmitting}
          connectionState={collabStatus}
          onDraftChange={(next) => {
            setCallsignDraft(next);
            setCallsignError(null);
          }}
          onSubmit={handleCallsignSubmit}
          onCancel={handleCallsignCancel}
        />
      )}
    </div>
  );
}

export default App;
